import { ReactNode, useEffect, useState } from 'react';
import { Drawer, Flex, Layout, Menu, Typography } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { PAGE_COMPONENT, PageName, useApp } from '@/App';
import { useUserData } from '@/libs/fleet-api/hooks';
import { unauthenticate } from '@/libs/fleet-api/services/users';
import versionData from '../../version.json';
import DebugPanel from './debug/DebugPanel';

export default function AppLayout({ children }: { children?: ReactNode }) {
	const appVersion = versionData?.version ?? '1.0.0';
	const { page, navigate } = useApp();
	const userData = useUserData();
	const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 1024);
	const [drawerOpen, setDrawerOpen] = useState(false);

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 1024px)');
		const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
		setIsMobile(mq.matches);
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, []);

	const mainMenu = [];

	switch (userData?.groupName) {
		case 'FRM':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: '#prescreening',
						label: 'Pre-Screening',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
						children: [
							{
								key: 'CustomerListPage',
								label: 'Data Customer',
							},
							{
								key: 'FRMProspectListPage',
								label: 'Prospects',
							},
						],
					},

					{
						key: 'FRMCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: 'FRMCustomerCheckingListPage',
						label: 'Customer Checking',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.7778 16.6667L14.3333 18.2222L19 13.5556M5 18.2222V17.4444C5 15.2967 6.74112 13.5556 8.88889 13.5556H11.8056M13.5556 8.11111C13.5556 9.82933 12.1626 11.2222 10.4444 11.2222C8.72622 11.2222 7.33333 9.82933 7.33333 8.11111C7.33333 6.39289 8.72622 5 10.4444 5C12.1626 5 13.5556 6.39289 13.5556 8.11111Z"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},

					{
						key: '#survey',
						label: 'Survey',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18.8618 6.11264C18.7285 5.79746 18.5068 5.5309 18.2275 5.34181C17.9482 5.15326 17.6082 5.0421 17.2465 5.0421H15.737V4.78167H13.5489C13.5588 4.71089 13.5644 4.63788 13.5644 4.56434C13.5643 3.70171 12.8626 3 12 3C11.1374 3 10.4357 3.70171 10.4357 4.56434C10.4357 4.63788 10.4412 4.71089 10.4512 4.78167H8.26308V5.0421H6.75346C6.51291 5.0421 6.28069 5.09133 6.07111 5.18033C5.75536 5.31361 5.48884 5.53533 5.29972 5.81459C5.11116 6.09438 5 6.43391 5 6.79556V19.3667C5 19.6072 5.04923 19.8389 5.1377 20.049C5.27151 20.3642 5.49326 20.6308 5.77252 20.8199C6.05179 21.009 6.39181 21.1196 6.75346 21.1196H17.2465C17.4871 21.1196 17.7193 21.0709 17.9295 20.9814C18.2446 20.8486 18.5112 20.6264 18.7003 20.3471C18.8888 20.0678 19 19.7278 19 19.3667V6.79556C19 6.55448 18.9508 6.32279 18.8618 6.11264ZM12 3.98373C12.3202 3.98373 12.5806 4.24416 12.5806 4.56434C12.5806 4.6412 12.5651 4.71418 12.5375 4.78167H11.4619C11.4348 4.71421 11.4194 4.6412 11.4194 4.56434C11.4194 4.24416 11.6804 3.98373 12 3.98373ZM17.9571 19.3667C17.9571 19.4662 17.9372 19.558 17.9013 19.6426C17.8482 19.7693 17.7569 19.8787 17.6436 19.9561C17.5297 20.0324 17.3959 20.0767 17.2465 20.0767H6.75346C6.65391 20.0767 6.56158 20.0568 6.47696 20.0214C6.35033 19.9677 6.24084 19.8765 6.16397 19.7631C6.0871 19.6492 6.04287 19.5154 6.04287 19.3667V6.79556C6.04287 6.69601 6.06279 6.60367 6.09871 6.51906C6.15236 6.39187 6.2425 6.28294 6.35639 6.20607C6.47031 6.12977 6.60411 6.08553 6.75342 6.08497H8.26301V6.30229C8.26301 6.71037 8.59369 7.04106 9.00234 7.04106H14.9976C15.4062 7.04106 15.7369 6.71037 15.7369 6.30229V6.08497H17.2465C17.3461 6.08497 17.4384 6.10489 17.523 6.14081C17.6496 6.19446 17.7591 6.2846 17.836 6.39848C17.9123 6.51293 17.9565 6.64621 17.9571 6.79552V19.3667Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 9.12683H8.21936V9.99554H15.7801V9.12683Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 11.5167H8.21936V12.3854H15.7801V11.5167Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 13.9067H8.21936V14.7755H15.7801V13.9067Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 16.948H12V17.8173H15.7801V16.948Z"
									fill="currentColor"
								/>
							</svg>
						),
						children: [
							{
								key: 'FRMPraSurveyPage',
								label: 'Data Pra Survey',
							},
							{
								key: 'FRMRequestSurveyPage',
								label: 'Request Survey',
							},
						],
					},

					{
						key: 'FRMVisitReportListPage',
						label: 'Input Laporan Kunjungan',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.835 5.498L15.252 2.95C15.0452 2.73208 14.7967 2.55785 14.5214 2.43762C14.2461 2.31738 13.9494 2.25359 13.649 2.25H6.768C6.16706 2.25185 5.59125 2.49139 5.16632 2.91632C4.74139 3.34125 4.50185 3.91706 4.5 4.518V18.182C4.50185 18.7829 4.74139 19.3587 5.16632 19.7837C5.59125 20.2086 6.16706 20.4481 6.768 20.45H16.232C16.8329 20.4481 17.4087 20.2086 17.8337 19.7837C18.2586 19.3587 18.4981 18.7829 18.5 18.182V7.101C18.501 6.80306 18.4427 6.5079 18.3285 6.2327C18.2143 5.95751 18.0466 5.70776 17.835 5.498ZM16.813 6.45H14.3V3.937L16.813 6.45ZM17.1 18.182C17.0982 18.4116 17.0061 18.6313 16.8437 18.7937C16.6813 18.9561 16.4616 19.0482 16.232 19.05H6.768C6.53836 19.0482 6.31864 18.9561 6.15625 18.7937C5.99387 18.6313 5.90183 18.4116 5.9 18.182V4.518C5.90183 4.28836 5.99387 4.06864 6.15625 3.90625C6.31864 3.74387 6.53836 3.65183 6.768 3.65H12.9V7.15C12.9 7.33565 12.9737 7.5137 13.105 7.64497C13.2363 7.77625 13.4143 7.85 13.6 7.85H17.1V18.182Z"
									fill="currentColor"
								/>
								<path
									d="M9.40006 11.553C9.26825 11.4212 9.08947 11.3472 8.90306 11.3472C8.71665 11.3472 8.53787 11.4212 8.40606 11.553C8.27425 11.6848 8.2002 11.8636 8.2002 12.05C8.2002 12.2364 8.27425 12.4152 8.40606 12.547L10.1561 14.297C10.2211 14.3626 10.2986 14.4147 10.3839 14.4502C10.4692 14.4858 10.5606 14.5041 10.6531 14.5041C10.7455 14.5041 10.837 14.4858 10.9223 14.4502C11.0076 14.4147 11.085 14.3626 11.1501 14.297L14.6501 10.797C14.7819 10.6578 14.853 10.4719 14.8477 10.2802C14.8425 10.0886 14.7613 9.90683 14.6221 9.77502C14.4828 9.64321 14.2969 9.57211 14.1053 9.57736C13.9136 9.58261 13.7319 9.66378 13.6001 9.80302L10.5971 12.813L9.40006 11.553Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'FRMCallReportListPage',
						label: 'Call Report',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15429)">
									<path
										d="M3.77579 5.79442L2.91892 5.47172C2.13575 5.17672 1.26173 5.57262 0.96677 6.35516C0.67177 7.13848 1.06786 8.01192 1.85095 8.30692L3.77579 9.0325V5.79442Z"
										fill="currentColor"
									/>
									<path
										d="M15.2129 6.41828C15.2042 6.44914 15.1958 6.4784 15.1873 6.50844V17.0174C15.1873 17.3086 15.0677 17.5762 14.8769 17.7665C14.6863 17.9579 14.419 18.0769 14.1279 18.0769H6.36642C6.40415 18.0153 6.43864 17.9505 6.4652 17.8801C6.51567 17.7453 6.53954 17.6082 6.53954 17.4734C6.53954 17.2412 6.46813 17.0144 6.33931 16.8241C6.20993 16.6338 6.0245 16.4817 5.79243 16.3943L4.59802 15.9445H4.59763C4.46325 15.8937 4.32653 15.8698 4.19153 15.8698C3.95896 15.8698 3.73192 15.941 3.54204 16.0701C3.35181 16.1995 3.19931 16.3851 3.11188 16.617C3.06165 16.7509 3.03735 16.8881 3.03735 17.0228C3.03735 17.2562 3.10923 17.4831 3.23802 17.6729C3.36743 17.8628 3.55286 18.0157 3.78489 18.1027L4.9795 18.5525C5.11411 18.6034 5.25122 18.6272 5.38579 18.6272C5.61845 18.6272 5.8454 18.5556 6.03532 18.427C6.06864 18.4042 6.09923 18.3777 6.13024 18.3515C7.08532 19.1541 8.5788 19.9443 10.8044 19.9975C15.4519 20.1076 16.688 16.6639 17.3596 12.889C17.3596 12.889 18.9632 5.65738 19.072 5.27531C19.6161 3.36433 16.2465 2.78824 15.2129 6.41828Z"
										fill="currentColor"
									/>
									<path
										d="M5.5854 9.69754V1.71988H13.3776V14.4811H6.70181C6.68657 14.6166 6.6556 14.7518 6.60556 14.8847C6.47923 15.2212 6.2547 15.4947 5.97802 15.6826C5.90521 15.7319 5.82915 15.775 5.7504 15.8123L5.97899 15.8982C6.31536 16.0246 6.58942 16.2495 6.77731 16.526C6.93001 16.7509 7.0261 17.0104 7.05712 17.2823H14.1278C14.2022 17.2823 14.2657 17.2531 14.315 17.2049C14.3635 17.1556 14.3924 17.0917 14.3924 17.0173V0.264923C14.3924 0.190235 14.3635 0.127032 14.315 0.0777353C14.2657 0.028829 14.2022 0.000313416 14.1278 -3.8147e-05H4.83517C4.76083 0.000313416 4.69712 0.028829 4.64798 0.0777353C4.59946 0.127032 4.57095 0.190196 4.57056 0.264923V9.27352L5.26415 9.53383C5.37895 9.57778 5.48603 9.63285 5.5854 9.69754ZM9.48142 16.4351C9.22528 16.4351 9.01794 16.2271 9.01794 15.9714C9.01794 15.7153 9.22528 15.5078 9.48142 15.5078C9.73731 15.5078 9.94505 15.7153 9.94505 15.9714C9.94505 16.2271 9.73731 16.4351 9.48142 16.4351Z"
										fill="currentColor"
									/>
									<path
										d="M4.62429 15.3707C4.7589 15.4211 4.89597 15.445 5.03054 15.445C5.26335 15.445 5.49019 15.3737 5.68003 15.2447C5.87026 15.1154 6.02241 14.9293 6.10999 14.6979C6.16046 14.5634 6.18433 14.4264 6.18433 14.2916C6.18433 14.0589 6.11312 13.8317 5.98409 13.6423C5.85491 13.452 5.66929 13.2995 5.43765 13.2121L4.2428 12.7619C4.10843 12.7115 3.97147 12.6876 3.83632 12.6876C3.60374 12.6876 3.37687 12.7588 3.18702 12.8878C2.99698 13.0172 2.84448 13.2032 2.75706 13.4351C2.70624 13.5691 2.68253 13.7062 2.68237 13.8414C2.68237 14.074 2.75397 14.3008 2.883 14.4911C3.01222 14.6814 3.19784 14.8331 3.43003 14.9209L4.62429 15.3707Z"
										fill="currentColor"
									/>
									<path
										d="M4.26427 12.1881C4.39888 12.2397 4.53595 12.2628 4.67052 12.2628C4.90313 12.2628 5.13052 12.192 5.3204 12.0625C5.51024 11.9331 5.66278 11.7479 5.74997 11.5164C5.80083 11.3808 5.82431 11.2441 5.82431 11.109C5.8247 10.8772 5.75329 10.6499 5.62446 10.4597C5.49509 10.2698 5.30927 10.1177 5.0772 10.0299L3.88278 9.58008C3.74798 9.52922 3.61126 9.50539 3.4763 9.50539C3.24372 9.50539 3.01685 9.5766 2.82685 9.70641C2.63696 9.83504 2.48446 10.0211 2.39665 10.2533C2.34642 10.3873 2.32251 10.5244 2.32251 10.6592C2.32251 10.8918 2.39395 11.1186 2.52298 11.3089C2.6522 11.4988 2.83802 11.6513 3.07009 11.739L4.26427 12.1881Z"
										fill="currentColor"
									/>
									<path
										d="M8.85315 7.36488L9.27429 7.09375C9.40929 7.0075 9.44854 6.82805 9.36171 6.69324L8.76612 5.76434C8.67968 5.62957 8.50019 5.59105 8.36522 5.67695C8.36214 5.67883 8.31499 5.70891 7.80526 6.03625C7.2114 6.4175 7.5305 8.05078 8.37812 9.37211C9.22593 10.6934 10.5785 11.6639 11.1719 11.2827C11.6819 10.9557 11.7286 10.9256 11.7319 10.923C11.8667 10.8371 11.906 10.6569 11.8195 10.5221L11.2235 9.59395C11.1373 9.45914 10.9576 9.41988 10.8226 9.50691L10.4013 9.77723C10.2785 9.85578 10.1158 9.83078 10.022 9.7191C10.022 9.7191 9.7389 9.4291 9.31624 8.77016C8.89378 8.11203 8.7478 7.73305 8.7478 7.73305C8.68507 7.60137 8.73034 7.44348 8.85315 7.36488Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15429">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: 'FRMPOListPage',
						label: 'Purchase Order (PO)',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
					{
						key: 'FRMSimulationListPage',
						label: 'Simulasi Kredit',
						icon: (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
							>
								<path
									d="M2.88445 17.1132C4.10484 18.3337 6.06902 18.3337 9.99739 18.3337C13.9257 18.3337 15.89 18.3337 17.1103 17.1132C18.3307 15.8929 18.3307 13.9287 18.3307 10.0003C18.3307 6.07195 18.3307 4.10777 17.1103 2.88738C15.89 1.66699 13.9257 1.66699 9.99739 1.66699C6.06902 1.66699 4.10484 1.66699 2.88445 2.88738C1.66406 4.10777 1.66406 6.07195 1.66406 10.0003C1.66406 13.9287 1.66406 15.8929 2.88445 17.1132Z"
									stroke="currentColor"
									strokeWidth="1.3"
								/>
								<path
									d="M15 7.08366H11.6667M15 12.0837H11.6667M15 14.5837H11.6667M8.33333 7.08367H6.66667M6.66667 7.08367H5M6.66667 7.08367V5.41699M6.66667 7.08367V8.75034M7.91667 12.0837L6.66667 13.3337M6.66667 13.3337L5.41668 14.5837M6.66667 13.3337L5.41667 12.0837M6.66667 13.3337L7.91666 14.5837"
									stroke="currentColor"
									strokeWidth="1.3"
									strokeLinecap="round"
								/>
							</svg>
						),
					},
					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'FRMPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Pipeline Golive',
							},
						],
					},
					{
						key: '#pengajuan-kondisi-khusus',
						label: 'Pengajuan Kondisi Khusus',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.785 8.45494C17.9033 8.45494 18.0162 8.39306 18.0997 8.30963L20.7697 5.63222C20.9435 5.45841 20.9435 5.17303 20.7697 4.99922L18.0997 2.34234C18.0162 2.25891 17.9033 2.22502 17.785 2.22502H13.734V1.3073C13.734 0.570094 13.1803 0 12.4429 0H12.0023C11.2648 0 10.619 0.570094 10.619 1.3073V2.22497H5.7701C5.52414 2.22497 5.27903 2.39433 5.27903 2.64009V7.98005C5.27903 8.22586 5.52414 8.45494 5.7701 8.45494H10.619V9.34495H6.21508C6.09686 9.34495 5.98389 9.37697 5.90046 9.46041L3.23046 12.123C3.05664 12.2968 3.05664 12.5746 3.23046 12.7485L5.90046 15.4315C5.98389 15.515 6.09691 15.5749 6.21508 15.5749H10.619V23.555C10.619 23.8008 10.818 24 11.064 24C11.3099 24 11.509 23.8007 11.509 23.555V15.5749H12.844V23.555C12.844 23.8007 13.043 24 13.2889 24C13.5349 24 13.7339 23.8007 13.7339 23.555V15.5749H18.2299C18.4759 15.5749 18.6288 15.3458 18.6288 15.1V9.76003C18.6288 9.51431 18.4759 9.34491 18.2299 9.34491H13.734V8.45489L17.785 8.45494ZM11.509 1.3073C11.509 1.06069 11.7558 0.890016 12.0022 0.890016H12.4428C12.6893 0.890016 12.844 1.06069 12.844 1.3073V2.22497H11.509V1.3073H11.509ZM17.739 10.2349V14.6849H6.39935L4.17438 12.4599L6.39935 10.235L17.739 10.2349ZM11.509 9.34495V8.45494H12.844V9.34495H11.509ZM6.16905 7.56492V3.11498H11.1101H13.3351H17.6008L19.8258 5.33995L17.6008 7.56492H6.16905Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: '#monitoring-prospect',
						label: 'Monitoring Prospect',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M1.875 3.75V13.125C1.875 13.8125 2.4375 14.375 3.125 14.375H16.875C17.5625 14.375 18.125 13.8125 18.125 13.125V3.75C18.125 3.0625 17.5625 2.5 16.875 2.5H3.125C2.4375 2.5 1.875 3.0625 1.875 3.75Z"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M5.625 18.125H14.375"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 14.375C8.125 15.6875 7.6875 17.25 7 18.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M13 18.125C12.3125 17.25 11.875 15.6875 11.875 14.375"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M6.875 6.25C5.5 6.25 4.375 7.375 4.375 8.75C4.375 10.125 5.5 11.25 6.875 11.25C8.25 11.25 9.375 10.125 9.375 8.75"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M7.5 5.625V8.125H10C10 6.75 8.875 5.625 7.5 5.625Z"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M12.5 7.5H15.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M14.375 9.375H15.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
					{
						key: 'NAPListPage',
						label: 'Nota Analisa Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'NAPListPage',
								label: 'NAP',
							},
							{
								key: 'MKLListPage',
								label: 'Memo Kredit Limit',
							},
						],
					},
					{
						key: 'SPFListPage',
						label: 'Pemberitahuan Fasilitas',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'MPPListPage',
						label: 'Pengajuan Pencairan Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
					// Quality Control & Activity Robotic dipindah ke role FCAS — role asli untuk fitur ini.
					// {
					// 	key: '#quality-control',
					// 	label: 'Quality Control',
					// 	icon: (...),
					// 	children: [
					// 		{ key: 'QCNewCustomerListPage', label: 'Quality Control New Customer' },
					// 		{ key: 'QCPraPencairanListPage', label: 'Quality Control Pra Pencairan' },
					// 	],
					// },
					// {
					// 	key: '#activity-robotic',
					// 	label: 'Activity Robotic',
					// 	icon: (...),
					// 	children: [
					// 		{ key: 'ActivityRoboticPraPencairanListPage', label: 'Activity Robotic Pra Pencairan' },
					// 	],
					// },
				],
			);

			break;
		case 'FCOH':
			mainMenu.push(
				{
					key: 'DashboardPage',
					label: 'Dashboard',
					icon: (
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
								fill="currentColor"
							/>
						</svg>
					),
				},
				{
					key: 'PrintListPage',
					label: 'Pencetakan Perjanjian Pembiayaan',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
				{
					key: 'DisbursementListPage',
					label: 'Pencetakan Dokumen Pencairan',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
				{
					key: 'VerifyDisbursementListPage',
					label: 'Verifikasi Golive Confirmation',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
			);
			mainMenu.push({
				key: 'DMSListPage',
				label: 'Document Management System',
				icon: (
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
							fill="currentColor"
						/>
					</svg>
				),
			});
			break;
		case 'FCDVS':
			mainMenu.push(
				{
					key: 'DashboardPage',
					label: 'Dashboard',
					icon: (
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
								fill="currentColor"
							/>
						</svg>
					),
				},
				{
					key: 'PrintListPage',
					label: 'Pencetakan Perjanjian Pembiayaan',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
				{
					key: 'DisbursementListPage',
					label: 'Pencetakan Dokumen Pencairan',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
				{
					key: 'VerifyDisbursementListPage',
					label: 'Upload Dokumen Kesepakatan',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
			);
			mainMenu.push({
				key: 'DMSListPage',
				label: 'Document Management System',
				icon: (
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
							fill="currentColor"
						/>
					</svg>
				),
			});
			break;
		case 'FCOM':
			mainMenu.push({
				key: 'VerifyDisbursementListPage',
				label: 'Approval Golive Confirmation',
				icon: (
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M11.25 8.75H13.125"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M11.25 11.25H13.125"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M10 13.75H13.125"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M8.125 7.5V8.125"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M8.125 11.875V12.5"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				),
			});
			mainMenu.push({
				key: 'DMSListPage',
				label: 'Document Management System',
				icon: (
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
							fill="currentColor"
						/>
					</svg>
				),
			});
			break;
		case 'FBDH':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHProspectListPage',
						label: 'Prospects',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: 'FBDHCustomerCheckingListPage',
						label: 'Customer Checking',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.7778 16.6667L14.3333 18.2222L19 13.5556M5 18.2222V17.4444C5 15.2967 6.74112 13.5556 8.88889 13.5556H11.8056M13.5556 8.11111C13.5556 9.82933 12.1626 11.2222 10.4444 11.2222C8.72622 11.2222 7.33333 9.82933 7.33333 8.11111C7.33333 6.39289 8.72622 5 10.4444 5C12.1626 5 13.5556 6.39289 13.5556 8.11111Z"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHRequestSurveyListPage',
						label: 'Survey',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18.8618 6.11264C18.7285 5.79746 18.5068 5.5309 18.2275 5.34181C17.9482 5.15326 17.6082 5.0421 17.2465 5.0421H15.737V4.78167H13.5489C13.5588 4.71089 13.5644 4.63788 13.5644 4.56434C13.5643 3.70171 12.8626 3 12 3C11.1374 3 10.4357 3.70171 10.4357 4.56434C10.4357 4.63788 10.4412 4.71089 10.4512 4.78167H8.26308V5.0421H6.75346C6.51291 5.0421 6.28069 5.09133 6.07111 5.18033C5.75536 5.31361 5.48884 5.53533 5.29972 5.81459C5.11116 6.09438 5 6.43391 5 6.79556V19.3667C5 19.6072 5.04923 19.8389 5.1377 20.049C5.27151 20.3642 5.49326 20.6308 5.77252 20.8199C6.05179 21.009 6.39181 21.1196 6.75346 21.1196H17.2465C17.4871 21.1196 17.7193 21.0709 17.9295 20.9814C18.2446 20.8486 18.5112 20.6264 18.7003 20.3471C18.8888 20.0678 19 19.7278 19 19.3667V6.79556C19 6.55448 18.9508 6.32279 18.8618 6.11264ZM12 3.98373C12.3202 3.98373 12.5806 4.24416 12.5806 4.56434C12.5806 4.6412 12.5651 4.71418 12.5375 4.78167H11.4619C11.4348 4.71421 11.4194 4.6412 11.4194 4.56434C11.4194 4.24416 11.6804 3.98373 12 3.98373ZM17.9571 19.3667C17.9571 19.4662 17.9372 19.558 17.9013 19.6426C17.8482 19.7693 17.7569 19.8787 17.6436 19.9561C17.5297 20.0324 17.3959 20.0767 17.2465 20.0767H6.75346C6.65391 20.0767 6.56158 20.0568 6.47696 20.0214C6.35033 19.9677 6.24084 19.8765 6.16397 19.7631C6.0871 19.6492 6.04287 19.5154 6.04287 19.3667V6.79556C6.04287 6.69601 6.06279 6.60367 6.09871 6.51906C6.15236 6.39187 6.2425 6.28294 6.35639 6.20607C6.47031 6.12977 6.60411 6.08553 6.75342 6.08497H8.26301V6.30229C8.26301 6.71037 8.59369 7.04106 9.00234 7.04106H14.9976C15.4062 7.04106 15.7369 6.71037 15.7369 6.30229V6.08497H17.2465C17.3461 6.08497 17.4384 6.10489 17.523 6.14081C17.6496 6.19446 17.7591 6.2846 17.836 6.39848C17.9123 6.51293 17.9565 6.64621 17.9571 6.79552V19.3667Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 9.12683H8.21936V9.99554H15.7801V9.12683Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 11.5167H8.21936V12.3854H15.7801V11.5167Z"
									fill="currentColor"
								/>

								<path
									d="M15.7801 13.9067H8.21936V14.7755H15.7801V13.9067Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 16.948H12V17.8173H15.7801V16.948Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: '#laporan-kunjungan',
						label: 'Laporan Kunjungan',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.835 5.498L15.252 2.95C15.0452 2.73208 14.7967 2.55785 14.5214 2.43762C14.2461 2.31738 13.9494 2.25359 13.649 2.25H6.768C6.16706 2.25185 5.59125 2.49139 5.16632 2.91632C4.74139 3.34125 4.50185 3.91706 4.5 4.518V18.182C4.50185 18.7829 4.74139 19.3587 5.16632 19.7837C5.59125 20.2086 6.16706 20.4481 6.768 20.45H16.232C16.8329 20.4481 17.4087 20.2086 17.8337 19.7837C18.2586 19.3587 18.4981 18.7829 18.5 18.182V7.101C18.501 6.80306 18.4427 6.5079 18.3285 6.2327C18.2143 5.95751 18.0466 5.70776 17.835 5.498ZM16.813 6.45H14.3V3.937L16.813 6.45ZM17.1 18.182C17.0982 18.4116 17.0061 18.6313 16.8437 18.7937C16.6813 18.9561 16.4616 19.0482 16.232 19.05H6.768C6.53836 19.0482 6.31864 18.9561 6.15625 18.7937C5.99387 18.6313 5.90183 18.4116 5.9 18.182V4.518C5.90183 4.28836 5.99387 4.06864 6.15625 3.90625C6.31864 3.74387 6.53836 3.65183 6.768 3.65H12.9V7.15C12.9 7.33565 12.9737 7.5137 13.105 7.64497C13.2363 7.77625 13.4143 7.85 13.6 7.85H17.1V18.182Z"
									fill="currentColor"
								/>
								<path
									d="M9.40006 11.553C9.26825 11.4212 9.08947 11.3472 8.90306 11.3472C8.71665 11.3472 8.53787 11.4212 8.40606 11.553C8.27425 11.6848 8.2002 11.8636 8.2002 12.05C8.2002 12.2364 8.27425 12.4152 8.40606 12.547L10.1561 14.297C10.2211 14.3626 10.2986 14.4147 10.3839 14.4502C10.4692 14.4858 10.5606 14.5041 10.6531 14.5041C10.7455 14.5041 10.837 14.4858 10.9223 14.4502C11.0076 14.4147 11.085 14.3626 11.1501 14.297L14.6501 10.797C14.7819 10.6578 14.853 10.4719 14.8477 10.2802C14.8425 10.0886 14.7613 9.90683 14.6221 9.77502C14.4828 9.64321 14.2969 9.57211 14.1053 9.57736C13.9136 9.58261 13.7319 9.66378 13.6001 9.80302L10.5971 12.813L9.40006 11.553Z"
									fill="currentColor"
								/>
							</svg>
						),
						children: [
							{
								key: 'FBDHVisitReportRejectListPage',
								label: 'Reject Laporan Kunjungan',
							},
							{
								key: 'FBDHVisitReportListPage',
								label: 'View Laporan Kunjungan',
							},
						],
					},
					{
						key: '#call-report',
						label: 'Call Report',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15429)">
									<path
										d="M3.77579 5.79442L2.91892 5.47172C2.13575 5.17672 1.26173 5.57262 0.96677 6.35516C0.67177 7.13848 1.06786 8.01192 1.85095 8.30692L3.77579 9.0325V5.79442Z"
										fill="currentColor"
									/>
									<path
										d="M15.2129 6.41828C15.2042 6.44914 15.1958 6.4784 15.1873 6.50844V17.0174C15.1873 17.3086 15.0677 17.5762 14.8769 17.7665C14.6863 17.9579 14.419 18.0769 14.1279 18.0769H6.36642C6.40415 18.0153 6.43864 17.9505 6.4652 17.8801C6.51567 17.7453 6.53954 17.6082 6.53954 17.4734C6.53954 17.2412 6.46813 17.0144 6.33931 16.8241C6.20993 16.6338 6.0245 16.4817 5.79243 16.3943L4.59802 15.9445H4.59763C4.46325 15.8937 4.32653 15.8698 4.19153 15.8698C3.95896 15.8698 3.73192 15.941 3.54204 16.0701C3.35181 16.1995 3.19931 16.3851 3.11188 16.617C3.06165 16.7509 3.03735 16.8881 3.03735 17.0228C3.03735 17.2562 3.10923 17.4831 3.23802 17.6729C3.36743 17.8628 3.55286 18.0157 3.78489 18.1027L4.9795 18.5525C5.11411 18.6034 5.25122 18.6272 5.38579 18.6272C5.61845 18.6272 5.8454 18.5556 6.03532 18.427C6.06864 18.4042 6.09923 18.3777 6.13024 18.3515C7.08532 19.1541 8.5788 19.9443 10.8044 19.9975C15.4519 20.1076 16.688 16.6639 17.3596 12.889C17.3596 12.889 18.9632 5.65738 19.072 5.27531C19.6161 3.36433 16.2465 2.78824 15.2129 6.41828Z"
										fill="currentColor"
									/>
									<path
										d="M5.5854 9.69754V1.71988H13.3776V14.4811H6.70181C6.68657 14.6166 6.6556 14.7518 6.60556 14.8847C6.47923 15.2212 6.2547 15.4947 5.97802 15.6826C5.90521 15.7319 5.82915 15.775 5.7504 15.8123L5.97899 15.8982C6.31536 16.0246 6.58942 16.2495 6.77731 16.526C6.93001 16.7509 7.0261 17.0104 7.05712 17.2823H14.1278C14.2022 17.2823 14.2657 17.2531 14.315 17.2049C14.3635 17.1556 14.3924 17.0917 14.3924 17.0173V0.264923C14.3924 0.190235 14.3635 0.127032 14.315 0.0777353C14.2657 0.028829 14.2022 0.000313416 14.1278 -3.8147e-05H4.83517C4.76083 0.000313416 4.69712 0.028829 4.64798 0.0777353C4.59946 0.127032 4.57095 0.190196 4.57056 0.264923V9.27352L5.26415 9.53383C5.37895 9.57778 5.48603 9.63285 5.5854 9.69754ZM9.48142 16.4351C9.22528 16.4351 9.01794 16.2271 9.01794 15.9714C9.01794 15.7153 9.22528 15.5078 9.48142 15.5078C9.73731 15.5078 9.94505 15.7153 9.94505 15.9714C9.94505 16.2271 9.73731 16.4351 9.48142 16.4351Z"
										fill="currentColor"
									/>
									<path
										d="M4.62429 15.3707C4.7589 15.4211 4.89597 15.445 5.03054 15.445C5.26335 15.445 5.49019 15.3737 5.68003 15.2447C5.87026 15.1154 6.02241 14.9293 6.10999 14.6979C6.16046 14.5634 6.18433 14.4264 6.18433 14.2916C6.18433 14.0589 6.11312 13.8317 5.98409 13.6423C5.85491 13.452 5.66929 13.2995 5.43765 13.2121L4.2428 12.7619C4.10843 12.7115 3.97147 12.6876 3.83632 12.6876C3.60374 12.6876 3.37687 12.7588 3.18702 12.8878C2.99698 13.0172 2.84448 13.2032 2.75706 13.4351C2.70624 13.5691 2.68253 13.7062 2.68237 13.8414C2.68237 14.074 2.75397 14.3008 2.883 14.4911C3.01222 14.6814 3.19784 14.8331 3.43003 14.9209L4.62429 15.3707Z"
										fill="currentColor"
									/>
									<path
										d="M4.26427 12.1881C4.39888 12.2397 4.53595 12.2628 4.67052 12.2628C4.90313 12.2628 5.13052 12.192 5.3204 12.0625C5.51024 11.9331 5.66278 11.7479 5.74997 11.5164C5.80083 11.3808 5.82431 11.2441 5.82431 11.109C5.8247 10.8772 5.75329 10.6499 5.62446 10.4597C5.49509 10.2698 5.30927 10.1177 5.0772 10.0299L3.88278 9.58008C3.74798 9.52922 3.61126 9.50539 3.4763 9.50539C3.24372 9.50539 3.01685 9.5766 2.82685 9.70641C2.63696 9.83504 2.48446 10.0211 2.39665 10.2533C2.34642 10.3873 2.32251 10.5244 2.32251 10.6592C2.32251 10.8918 2.39395 11.1186 2.52298 11.3089C2.6522 11.4988 2.83802 11.6513 3.07009 11.739L4.26427 12.1881Z"
										fill="currentColor"
									/>
									<path
										d="M8.85315 7.36488L9.27429 7.09375C9.40929 7.0075 9.44854 6.82805 9.36171 6.69324L8.76612 5.76434C8.67968 5.62957 8.50019 5.59105 8.36522 5.67695C8.36214 5.67883 8.31499 5.70891 7.80526 6.03625C7.2114 6.4175 7.5305 8.05078 8.37812 9.37211C9.22593 10.6934 10.5785 11.6639 11.1719 11.2827C11.6819 10.9557 11.7286 10.9256 11.7319 10.923C11.8667 10.8371 11.906 10.6569 11.8195 10.5221L11.2235 9.59395C11.1373 9.45914 10.9576 9.41988 10.8226 9.50691L10.4013 9.77723C10.2785 9.85578 10.1158 9.83078 10.022 9.7191C10.022 9.7191 9.7389 9.4291 9.31624 8.77016C8.89378 8.11203 8.7478 7.73305 8.7478 7.73305C8.68507 7.60137 8.73034 7.44348 8.85315 7.36488Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15429">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},

					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							// {
							// 	key: 'FBDHPipelineListPage',
							// 	label: 'Summary Pipeline',
							// },
							// {
							// 	key: 'FBDHPipelineListPageJF',
							// 	label: 'JF-Commercial',
							// },
							{
								key: 'FBDHPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Pipeline Golive',
							},
						],
					},
					{
						key: '#pengajuan-kondisi-khusus',
						label: 'Pengajuan Kondisi Khusus',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.785 8.45494C17.9033 8.45494 18.0162 8.39306 18.0997 8.30963L20.7697 5.63222C20.9435 5.45841 20.9435 5.17303 20.7697 4.99922L18.0997 2.34234C18.0162 2.25891 17.9033 2.22502 17.785 2.22502H13.734V1.3073C13.734 0.570094 13.1803 0 12.4429 0H12.0023C11.2648 0 10.619 0.570094 10.619 1.3073V2.22497H5.7701C5.52414 2.22497 5.27903 2.39433 5.27903 2.64009V7.98005C5.27903 8.22586 5.52414 8.45494 5.7701 8.45494H10.619V9.34495H6.21508C6.09686 9.34495 5.98389 9.37697 5.90046 9.46041L3.23046 12.123C3.05664 12.2968 3.05664 12.5746 3.23046 12.7485L5.90046 15.4315C5.98389 15.515 6.09691 15.5749 6.21508 15.5749H10.619V23.555C10.619 23.8008 10.818 24 11.064 24C11.3099 24 11.509 23.8007 11.509 23.555V15.5749H12.844V23.555C12.844 23.8007 13.043 24 13.2889 24C13.5349 24 13.7339 23.8007 13.7339 23.555V15.5749H18.2299C18.4759 15.5749 18.6288 15.3458 18.6288 15.1V9.76003C18.6288 9.51431 18.4759 9.34491 18.2299 9.34491H13.734V8.45489L17.785 8.45494ZM11.509 1.3073C11.509 1.06069 11.7558 0.890016 12.0022 0.890016H12.4428C12.6893 0.890016 12.844 1.06069 12.844 1.3073V2.22497H11.509V1.3073H11.509ZM17.739 10.2349V14.6849H6.39935L4.17438 12.4599L6.39935 10.235L17.739 10.2349ZM11.509 9.34495V8.45494H12.844V9.34495H11.509ZM6.16905 7.56492V3.11498H11.1101H13.3351H17.6008L19.8258 5.33995L17.6008 7.56492H6.16905Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'NAPListPage',
						label: 'Nota Analisa Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'NAPListPage',
								label: 'NAP',
							},
							{
								key: 'MKLListPage',
								label: 'Memo Kredit Limit',
							},
						],
					},
					{
						key: 'SPFListPage',
						label: 'Pemberitahuan Fasilitas',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'MPPListPage',
						label: 'Pengajuan Pencairan Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
					{
						key: 'FBDHPOListPage',
						label: 'Purchase Order (PO)',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
				],
			);

			break;
		case 'FCAS':
			mainMenu.push(
				{
					key: 'DashboardPage',
					label: 'Dashboard',
					icon: (
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
								fill="currentColor"
							/>
						</svg>
					),
				},
				{
					key: 'DisbursementListPage',
					label: 'Pencetakan Dokumen Pencairan',
					icon: (
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 8.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M11.25 11.25H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M10 13.75H13.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 7.5V8.125"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.125 11.875V12.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
								stroke="currentColor"
								strokeMiterlimit="10"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					),
				},
				{
					key: '#quality-control',
					label: 'Quality Control',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M7.99271 4.50172C7.77673 3.74107 7.07635 3.18213 6.24742 3.18213C5.41849 3.18213 4.71815 3.74107 4.50214 4.50172H2V5.49141H4.50214C4.71812 6.25205 5.41849 6.81099 6.24742 6.81099C7.07635 6.81099 7.77669 6.25205 7.99271 5.49141H18V4.50172H7.99271ZM6.24742 5.8213C5.79266 5.8213 5.42268 5.45132 5.42268 4.99656C5.42268 4.5418 5.79266 4.17182 6.24742 4.17182C6.70219 4.17182 7.07217 4.5418 7.07217 4.99656C7.07217 5.45132 6.70219 5.8213 6.24742 5.8213Z"
									fill="currentColor"
								/>
								<path
									d="M10 8.18555C9.17107 8.18555 8.47073 8.74449 8.25471 9.50513H2V10.4948H8.25471C8.4707 11.2555 9.17107 11.8144 10 11.8144C10.8289 11.8144 11.5293 11.2555 11.7453 10.4948H18V9.50513H11.7453C11.5293 8.74449 10.8289 8.18555 10 8.18555ZM10 10.8247C9.54524 10.8247 9.17526 10.4547 9.17526 9.99998C9.17526 9.54522 9.54524 9.17524 10 9.17524C10.4548 9.17524 10.8247 9.54522 10.8247 9.99998C10.8247 10.4547 10.4548 10.8247 10 10.8247Z"
									fill="currentColor"
								/>
								<path
									d="M6.24742 13.1887C5.41849 13.1887 4.71815 13.7477 4.50214 14.5083H2V15.498H4.50214C4.71812 16.2586 5.41849 16.8176 6.24742 16.8176C7.07635 16.8176 7.77669 16.2586 7.99271 15.498H18V14.5083H7.99271C7.77669 13.7477 7.07635 13.1887 6.24742 13.1887ZM6.24742 15.8279C5.79266 15.8279 5.42268 15.4579 5.42268 15.0032C5.42268 14.5484 5.79266 14.1784 6.24742 14.1784C6.70219 14.1784 7.07217 14.5484 7.07217 15.0032C7.07217 15.4579 6.70219 15.8279 6.24742 15.8279Z"
									fill="currentColor"
								/>
							</svg>
						),
					children: [
						{ key: 'QCNewCustomerListPage', label: 'Quality Control New Customer' },
						{ key: 'QCPraPencairanListPage', label: 'Quality Control Pra Pencairan' },
					],
				},
				{
					key: '#activity-robotic',
					label: 'Activity Robotic',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit={10}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					children: [
						{ key: 'ActivityRoboticPraPencairanListPage', label: 'Activity Robotic Pra Pencairan' },
					],
				},
			);
			break;
		case 'CCDH':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'CCDHProspectListPage',
						label: 'Prospects',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: '#customer-checking',
						label: 'Customer Checking',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.7778 16.6667L14.3333 18.2222L19 13.5556M5 18.2222V17.4444C5 15.2967 6.74112 13.5556 8.88889 13.5556H11.8056M13.5556 8.11111C13.5556 9.82933 12.1626 11.2222 10.4444 11.2222C8.72622 11.2222 7.33333 9.82933 7.33333 8.11111C7.33333 6.39289 8.72622 5 10.4444 5C12.1626 5 13.5556 6.39289 13.5556 8.11111Z"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'CCDHAssignCCAListPage',
								label: 'Assign CCA',
							},
							{
								key: 'CCDHAnalisaSLIKListPage',
								label: 'Analisa SLIK & Name Clearance',
							},
						],
					},

					{
						key: 'CCDHSurveyApprovalListPage',
						label: 'Survey',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18.8618 6.11264C18.7285 5.79746 18.5068 5.5309 18.2275 5.34181C17.9482 5.15326 17.6082 5.0421 17.2465 5.0421H15.737V4.78167H13.5489C13.5588 4.71089 13.5644 4.63788 13.5644 4.56434C13.5643 3.70171 12.8626 3 12 3C11.1374 3 10.4357 3.70171 10.4357 4.56434C10.4357 4.63788 10.4412 4.71089 10.4512 4.78167H8.26308V5.0421H6.75346C6.51291 5.0421 6.28069 5.09133 6.07111 5.18033C5.75536 5.31361 5.48884 5.53533 5.29972 5.81459C5.11116 6.09438 5 6.43391 5 6.79556V19.3667C5 19.6072 5.04923 19.8389 5.1377 20.049C5.27151 20.3642 5.49326 20.6308 5.77252 20.8199C6.05179 21.009 6.39181 21.1196 6.75346 21.1196H17.2465C17.4871 21.1196 17.7193 21.0709 17.9295 20.9814C18.2446 20.8486 18.5112 20.6264 18.7003 20.3471C18.8888 20.0678 19 19.7278 19 19.3667V6.79556C19 6.55448 18.9508 6.32279 18.8618 6.11264ZM12 3.98373C12.3202 3.98373 12.5806 4.24416 12.5806 4.56434C12.5806 4.6412 12.5651 4.71418 12.5375 4.78167H11.4619C11.4348 4.71421 11.4194 4.6412 11.4194 4.56434C11.4194 4.24416 11.6804 3.98373 12 3.98373ZM17.9571 19.3667C17.9571 19.4662 17.9372 19.558 17.9013 19.6426C17.8482 19.7693 17.7569 19.8787 17.6436 19.9561C17.5297 20.0324 17.3959 20.0767 17.2465 20.0767H6.75346C6.65391 20.0767 6.56158 20.0568 6.47696 20.0214C6.35033 19.9677 6.24084 19.8765 6.16397 19.7631C6.0871 19.6492 6.04287 19.5154 6.04287 19.3667V6.79556C6.04287 6.69601 6.06279 6.60367 6.09871 6.51906C6.15236 6.39187 6.2425 6.28294 6.35639 6.20607C6.47031 6.12977 6.60411 6.08553 6.75342 6.08497H8.26301V6.30229C8.26301 6.71037 8.59369 7.04106 9.00234 7.04106H14.9976C15.4062 7.04106 15.7369 6.71037 15.7369 6.30229V6.08497H17.2465C17.3461 6.08497 17.4384 6.10489 17.523 6.14081C17.6496 6.19446 17.7591 6.2846 17.836 6.39848C17.9123 6.51293 17.9565 6.64621 17.9571 6.79552V19.3667Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 9.12683H8.21936V9.99554H15.7801V9.12683Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 11.5167H8.21936V12.3854H15.7801V11.5167Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 13.9067H8.21936V14.7755H15.7801V13.9067Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 16.948H12V17.8173H15.7801V16.948Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'CCDHVisitReportListPage',
						label: 'Laporan Kunjungan',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.835 5.498L15.252 2.95C15.0452 2.73208 14.7967 2.55785 14.5214 2.43762C14.2461 2.31738 13.9494 2.25359 13.649 2.25H6.768C6.16706 2.25185 5.59125 2.49139 5.16632 2.91632C4.74139 3.34125 4.50185 3.91706 4.5 4.518V18.182C4.50185 18.7829 4.74139 19.3587 5.16632 19.7837C5.59125 20.2086 6.16706 20.4481 6.768 20.45H16.232C16.8329 20.4481 17.4087 20.2086 17.8337 19.7837C18.2586 19.3587 18.4981 18.7829 18.5 18.182V7.101C18.501 6.80306 18.4427 6.5079 18.3285 6.2327C18.2143 5.95751 18.0466 5.70776 17.835 5.498ZM16.813 6.45H14.3V3.937L16.813 6.45ZM17.1 18.182C17.0982 18.4116 17.0061 18.6313 16.8437 18.7937C16.6813 18.9561 16.4616 19.0482 16.232 19.05H6.768C6.53836 19.0482 6.31864 18.9561 6.15625 18.7937C5.99387 18.6313 5.90183 18.4116 5.9 18.182V4.518C5.90183 4.28836 5.99387 4.06864 6.15625 3.90625C6.31864 3.74387 6.53836 3.65183 6.768 3.65H12.9V7.15C12.9 7.33565 12.9737 7.5137 13.105 7.64497C13.2363 7.77625 13.4143 7.85 13.6 7.85H17.1V18.182Z"
									fill="currentColor"
								/>
								<path
									d="M9.40006 11.553C9.26825 11.4212 9.08947 11.3472 8.90306 11.3472C8.71665 11.3472 8.53787 11.4212 8.40606 11.553C8.27425 11.6848 8.2002 11.8636 8.2002 12.05C8.2002 12.2364 8.27425 12.4152 8.40606 12.547L10.1561 14.297C10.2211 14.3626 10.2986 14.4147 10.3839 14.4502C10.4692 14.4858 10.5606 14.5041 10.6531 14.5041C10.7455 14.5041 10.837 14.4858 10.9223 14.4502C11.0076 14.4147 11.085 14.3626 11.1501 14.297L14.6501 10.797C14.7819 10.6578 14.853 10.4719 14.8477 10.2802C14.8425 10.0886 14.7613 9.90683 14.6221 9.77502C14.4828 9.64321 14.2969 9.57211 14.1053 9.57736C13.9136 9.58261 13.7319 9.66378 13.6001 9.80302L10.5971 12.813L9.40006 11.553Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: '#call-report',
						label: 'Call Report',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15429)">
									<path
										d="M3.77579 5.79442L2.91892 5.47172C2.13575 5.17672 1.26173 5.57262 0.96677 6.35516C0.67177 7.13848 1.06786 8.01192 1.85095 8.30692L3.77579 9.0325V5.79442Z"
										fill="currentColor"
									/>
									<path
										d="M15.2129 6.41828C15.2042 6.44914 15.1958 6.4784 15.1873 6.50844V17.0174C15.1873 17.3086 15.0677 17.5762 14.8769 17.7665C14.6863 17.9579 14.419 18.0769 14.1279 18.0769H6.36642C6.40415 18.0153 6.43864 17.9505 6.4652 17.8801C6.51567 17.7453 6.53954 17.6082 6.53954 17.4734C6.53954 17.2412 6.46813 17.0144 6.33931 16.8241C6.20993 16.6338 6.0245 16.4817 5.79243 16.3943L4.59802 15.9445H4.59763C4.46325 15.8937 4.32653 15.8698 4.19153 15.8698C3.95896 15.8698 3.73192 15.941 3.54204 16.0701C3.35181 16.1995 3.19931 16.3851 3.11188 16.617C3.06165 16.7509 3.03735 16.8881 3.03735 17.0228C3.03735 17.2562 3.10923 17.4831 3.23802 17.6729C3.36743 17.8628 3.55286 18.0157 3.78489 18.1027L4.9795 18.5525C5.11411 18.6034 5.25122 18.6272 5.38579 18.6272C5.61845 18.6272 5.8454 18.5556 6.03532 18.427C6.06864 18.4042 6.09923 18.3777 6.13024 18.3515C7.08532 19.1541 8.5788 19.9443 10.8044 19.9975C15.4519 20.1076 16.688 16.6639 17.3596 12.889C17.3596 12.889 18.9632 5.65738 19.072 5.27531C19.6161 3.36433 16.2465 2.78824 15.2129 6.41828Z"
										fill="currentColor"
									/>
									<path
										d="M5.5854 9.69754V1.71988H13.3776V14.4811H6.70181C6.68657 14.6166 6.6556 14.7518 6.60556 14.8847C6.47923 15.2212 6.2547 15.4947 5.97802 15.6826C5.90521 15.7319 5.82915 15.775 5.7504 15.8123L5.97899 15.8982C6.31536 16.0246 6.58942 16.2495 6.77731 16.526C6.93001 16.7509 7.0261 17.0104 7.05712 17.2823H14.1278C14.2022 17.2823 14.2657 17.2531 14.315 17.2049C14.3635 17.1556 14.3924 17.0917 14.3924 17.0173V0.264923C14.3924 0.190235 14.3635 0.127032 14.315 0.0777353C14.2657 0.028829 14.2022 0.000313416 14.1278 -3.8147e-05H4.83517C4.76083 0.000313416 4.69712 0.028829 4.64798 0.0777353C4.59946 0.127032 4.57095 0.190196 4.57056 0.264923V9.27352L5.26415 9.53383C5.37895 9.57778 5.48603 9.63285 5.5854 9.69754ZM9.48142 16.4351C9.22528 16.4351 9.01794 16.2271 9.01794 15.9714C9.01794 15.7153 9.22528 15.5078 9.48142 15.5078C9.73731 15.5078 9.94505 15.7153 9.94505 15.9714C9.94505 16.2271 9.73731 16.4351 9.48142 16.4351Z"
										fill="currentColor"
									/>
									<path
										d="M4.62429 15.3707C4.7589 15.4211 4.89597 15.445 5.03054 15.445C5.26335 15.445 5.49019 15.3737 5.68003 15.2447C5.87026 15.1154 6.02241 14.9293 6.10999 14.6979C6.16046 14.5634 6.18433 14.4264 6.18433 14.2916C6.18433 14.0589 6.11312 13.8317 5.98409 13.6423C5.85491 13.452 5.66929 13.2995 5.43765 13.2121L4.2428 12.7619C4.10843 12.7115 3.97147 12.6876 3.83632 12.6876C3.60374 12.6876 3.37687 12.7588 3.18702 12.8878C2.99698 13.0172 2.84448 13.2032 2.75706 13.4351C2.70624 13.5691 2.68253 13.7062 2.68237 13.8414C2.68237 14.074 2.75397 14.3008 2.883 14.4911C3.01222 14.6814 3.19784 14.8331 3.43003 14.9209L4.62429 15.3707Z"
										fill="currentColor"
									/>
									<path
										d="M4.26427 12.1881C4.39888 12.2397 4.53595 12.2628 4.67052 12.2628C4.90313 12.2628 5.13052 12.192 5.3204 12.0625C5.51024 11.9331 5.66278 11.7479 5.74997 11.5164C5.80083 11.3808 5.82431 11.2441 5.82431 11.109C5.8247 10.8772 5.75329 10.6499 5.62446 10.4597C5.49509 10.2698 5.30927 10.1177 5.0772 10.0299L3.88278 9.58008C3.74798 9.52922 3.61126 9.50539 3.4763 9.50539C3.24372 9.50539 3.01685 9.5766 2.82685 9.70641C2.63696 9.83504 2.48446 10.0211 2.39665 10.2533C2.34642 10.3873 2.32251 10.5244 2.32251 10.6592C2.32251 10.8918 2.39395 11.1186 2.52298 11.3089C2.6522 11.4988 2.83802 11.6513 3.07009 11.739L4.26427 12.1881Z"
										fill="currentColor"
									/>
									<path
										d="M8.85315 7.36488L9.27429 7.09375C9.40929 7.0075 9.44854 6.82805 9.36171 6.69324L8.76612 5.76434C8.67968 5.62957 8.50019 5.59105 8.36522 5.67695C8.36214 5.67883 8.31499 5.70891 7.80526 6.03625C7.2114 6.4175 7.5305 8.05078 8.37812 9.37211C9.22593 10.6934 10.5785 11.6639 11.1719 11.2827C11.6819 10.9557 11.7286 10.9256 11.7319 10.923C11.8667 10.8371 11.906 10.6569 11.8195 10.5221L11.2235 9.59395C11.1373 9.45914 10.9576 9.41988 10.8226 9.50691L10.4013 9.77723C10.2785 9.85578 10.1158 9.83078 10.022 9.7191C10.022 9.7191 9.7389 9.4291 9.31624 8.77016C8.89378 8.11203 8.7478 7.73305 8.7478 7.73305C8.68507 7.60137 8.73034 7.44348 8.85315 7.36488Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15429">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},

					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'CCDHPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Monitoring Pipeline Golive',
							},
						],
					},
					{
						key: '#pengajuan-kondisi-khusus',
						label: 'Pengajuan Kondisi Khusus',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.785 8.45494C17.9033 8.45494 18.0162 8.39306 18.0997 8.30963L20.7697 5.63222C20.9435 5.45841 20.9435 5.17303 20.7697 4.99922L18.0997 2.34234C18.0162 2.25891 17.9033 2.22502 17.785 2.22502H13.734V1.3073C13.734 0.570094 13.1803 0 12.4429 0H12.0023C11.2648 0 10.619 0.570094 10.619 1.3073V2.22497H5.7701C5.52414 2.22497 5.27903 2.39433 5.27903 2.64009V7.98005C5.27903 8.22586 5.52414 8.45494 5.7701 8.45494H10.619V9.34495H6.21508C6.09686 9.34495 5.98389 9.37697 5.90046 9.46041L3.23046 12.123C3.05664 12.2968 3.05664 12.5746 3.23046 12.7485L5.90046 15.4315C5.98389 15.515 6.09691 15.5749 6.21508 15.5749H10.619V23.555C10.619 23.8008 10.818 24 11.064 24C11.3099 24 11.509 23.8007 11.509 23.555V15.5749H12.844V23.555C12.844 23.8007 13.043 24 13.2889 24C13.5349 24 13.7339 23.8007 13.7339 23.555V15.5749H18.2299C18.4759 15.5749 18.6288 15.3458 18.6288 15.1V9.76003C18.6288 9.51431 18.4759 9.34491 18.2299 9.34491H13.734V8.45489L17.785 8.45494ZM11.509 1.3073C11.509 1.06069 11.7558 0.890016 12.0022 0.890016H12.4428C12.6893 0.890016 12.844 1.06069 12.844 1.3073V2.22497H11.509V1.3073H11.509ZM17.739 10.2349V14.6849H6.39935L4.17438 12.4599L6.39935 10.235L17.739 10.2349ZM11.509 9.34495V8.45494H12.844V9.34495H11.509ZM6.16905 7.56492V3.11498H11.1101H13.3351H17.6008L19.8258 5.33995L17.6008 7.56492H6.16905Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'NAPListPage',
						label: 'Nota Analisa Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'NAPListPage',
								label: 'NAP',
							},
							{
								key: 'MKLListPage',
								label: 'Memo Kredit Limit',
							},
						],
					},
					{
						key: 'SPFListPage',
						label: 'Pemberitahuan Fasilitas',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'MPPListPage',
						label: 'Pengajuan Pencairan Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
				],
			);

			break;
		case 'CCA':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'CCAProspectListPage',
						label: 'Prospects',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: '#customer-checking',
						label: 'Customer Checking',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.7778 16.6667L14.3333 18.2222L19 13.5556M5 18.2222V17.4444C5 15.2967 6.74112 13.5556 8.88889 13.5556H11.8056M13.5556 8.11111C13.5556 9.82933 12.1626 11.2222 10.4444 11.2222C8.72622 11.2222 7.33333 9.82933 7.33333 8.11111C7.33333 6.39289 8.72622 5 10.4444 5C12.1626 5 13.5556 6.39289 13.5556 8.11111Z"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'CCARequestSLIKPage',
								label: 'Request SLIK',
							},
							{
								key: 'CCAAnalisaSLIKPage',
								label: 'Analisa SLIK & Name Clearance',
							},
						],
					},

					{
						key: 'CCASurveyVerificationListPage',
						label: 'Survey',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18.8618 6.11264C18.7285 5.79746 18.5068 5.5309 18.2275 5.34181C17.9482 5.15326 17.6082 5.0421 17.2465 5.0421H15.737V4.78167H13.5489C13.5588 4.71089 13.5644 4.63788 13.5644 4.56434C13.5643 3.70171 12.8626 3 12 3C11.1374 3 10.4357 3.70171 10.4357 4.56434C10.4357 4.63788 10.4412 4.71089 10.4512 4.78167H8.26308V5.0421H6.75346C6.51291 5.0421 6.28069 5.09133 6.07111 5.18033C5.75536 5.31361 5.48884 5.53533 5.29972 5.81459C5.11116 6.09438 5 6.43391 5 6.79556V19.3667C5 19.6072 5.04923 19.8389 5.1377 20.049C5.27151 20.3642 5.49326 20.6308 5.77252 20.8199C6.05179 21.009 6.39181 21.1196 6.75346 21.1196H17.2465C17.4871 21.1196 17.7193 21.0709 17.9295 20.9814C18.2446 20.8486 18.5112 20.6264 18.7003 20.3471C18.8888 20.0678 19 19.7278 19 19.3667V6.79556C19 6.55448 18.9508 6.32279 18.8618 6.11264ZM12 3.98373C12.3202 3.98373 12.5806 4.24416 12.5806 4.56434C12.5806 4.6412 12.5651 4.71418 12.5375 4.78167H11.4619C11.4348 4.71421 11.4194 4.6412 11.4194 4.56434C11.4194 4.24416 11.6804 3.98373 12 3.98373ZM17.9571 19.3667C17.9571 19.4662 17.9372 19.558 17.9013 19.6426C17.8482 19.7693 17.7569 19.8787 17.6436 19.9561C17.5297 20.0324 17.3959 20.0767 17.2465 20.0767H6.75346C6.65391 20.0767 6.56158 20.0568 6.47696 20.0214C6.35033 19.9677 6.24084 19.8765 6.16397 19.7631C6.0871 19.6492 6.04287 19.5154 6.04287 19.3667V6.79556C6.04287 6.69601 6.06279 6.60367 6.09871 6.51906C6.15236 6.39187 6.2425 6.28294 6.35639 6.20607C6.47031 6.12977 6.60411 6.08553 6.75342 6.08497H8.26301V6.30229C8.26301 6.71037 8.59369 7.04106 9.00234 7.04106H14.9976C15.4062 7.04106 15.7369 6.71037 15.7369 6.30229V6.08497H17.2465C17.3461 6.08497 17.4384 6.10489 17.523 6.14081C17.6496 6.19446 17.7591 6.2846 17.836 6.39848C17.9123 6.51293 17.9565 6.64621 17.9571 6.79552V19.3667Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 9.12683H8.21936V9.99554H15.7801V9.12683Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 11.5167H8.21936V12.3854H15.7801V11.5167Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 13.9067H8.21936V14.7755H15.7801V13.9067Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 16.948H12V17.8173H15.7801V16.948Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'CCAVisitReportListPage',
						label: 'Laporan Kunjungan',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.835 5.498L15.252 2.95C15.0452 2.73208 14.7967 2.55785 14.5214 2.43762C14.2461 2.31738 13.9494 2.25359 13.649 2.25H6.768C6.16706 2.25185 5.59125 2.49139 5.16632 2.91632C4.74139 3.34125 4.50185 3.91706 4.5 4.518V18.182C4.50185 18.7829 4.74139 19.3587 5.16632 19.7837C5.59125 20.2086 6.16706 20.4481 6.768 20.45H16.232C16.8329 20.4481 17.4087 20.2086 17.8337 19.7837C18.2586 19.3587 18.4981 18.7829 18.5 18.182V7.101C18.501 6.80306 18.4427 6.5079 18.3285 6.2327C18.2143 5.95751 18.0466 5.70776 17.835 5.498ZM16.813 6.45H14.3V3.937L16.813 6.45ZM17.1 18.182C17.0982 18.4116 17.0061 18.6313 16.8437 18.7937C16.6813 18.9561 16.4616 19.0482 16.232 19.05H6.768C6.53836 19.0482 6.31864 18.9561 6.15625 18.7937C5.99387 18.6313 5.90183 18.4116 5.9 18.182V4.518C5.90183 4.28836 5.99387 4.06864 6.15625 3.90625C6.31864 3.74387 6.53836 3.65183 6.768 3.65H12.9V7.15C12.9 7.33565 12.9737 7.5137 13.105 7.64497C13.2363 7.77625 13.4143 7.85 13.6 7.85H17.1V18.182Z"
									fill="currentColor"
								/>
								<path
									d="M9.40006 11.553C9.26825 11.4212 9.08947 11.3472 8.90306 11.3472C8.71665 11.3472 8.53787 11.4212 8.40606 11.553C8.27425 11.6848 8.2002 11.8636 8.2002 12.05C8.2002 12.2364 8.27425 12.4152 8.40606 12.547L10.1561 14.297C10.2211 14.3626 10.2986 14.4147 10.3839 14.4502C10.4692 14.4858 10.5606 14.5041 10.6531 14.5041C10.7455 14.5041 10.837 14.4858 10.9223 14.4502C11.0076 14.4147 11.085 14.3626 11.1501 14.297L14.6501 10.797C14.7819 10.6578 14.853 10.4719 14.8477 10.2802C14.8425 10.0886 14.7613 9.90683 14.6221 9.77502C14.4828 9.64321 14.2969 9.57211 14.1053 9.57736C13.9136 9.58261 13.7319 9.66378 13.6001 9.80302L10.5971 12.813L9.40006 11.553Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: '#call-report',
						label: 'Call Report',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15429)">
									<path
										d="M3.77579 5.79442L2.91892 5.47172C2.13575 5.17672 1.26173 5.57262 0.96677 6.35516C0.67177 7.13848 1.06786 8.01192 1.85095 8.30692L3.77579 9.0325V5.79442Z"
										fill="currentColor"
									/>
									<path
										d="M15.2129 6.41828C15.2042 6.44914 15.1958 6.4784 15.1873 6.50844V17.0174C15.1873 17.3086 15.0677 17.5762 14.8769 17.7665C14.6863 17.9579 14.419 18.0769 14.1279 18.0769H6.36642C6.40415 18.0153 6.43864 17.9505 6.4652 17.8801C6.51567 17.7453 6.53954 17.6082 6.53954 17.4734C6.53954 17.2412 6.46813 17.0144 6.33931 16.8241C6.20993 16.6338 6.0245 16.4817 5.79243 16.3943L4.59802 15.9445H4.59763C4.46325 15.8937 4.32653 15.8698 4.19153 15.8698C3.95896 15.8698 3.73192 15.941 3.54204 16.0701C3.35181 16.1995 3.19931 16.3851 3.11188 16.617C3.06165 16.7509 3.03735 16.8881 3.03735 17.0228C3.03735 17.2562 3.10923 17.4831 3.23802 17.6729C3.36743 17.8628 3.55286 18.0157 3.78489 18.1027L4.9795 18.5525C5.11411 18.6034 5.25122 18.6272 5.38579 18.6272C5.61845 18.6272 5.8454 18.5556 6.03532 18.427C6.06864 18.4042 6.09923 18.3777 6.13024 18.3515C7.08532 19.1541 8.5788 19.9443 10.8044 19.9975C15.4519 20.1076 16.688 16.6639 17.3596 12.889C17.3596 12.889 18.9632 5.65738 19.072 5.27531C19.6161 3.36433 16.2465 2.78824 15.2129 6.41828Z"
										fill="currentColor"
									/>
									<path
										d="M5.5854 9.69754V1.71988H13.3776V14.4811H6.70181C6.68657 14.6166 6.6556 14.7518 6.60556 14.8847C6.47923 15.2212 6.2547 15.4947 5.97802 15.6826C5.90521 15.7319 5.82915 15.775 5.7504 15.8123L5.97899 15.8982C6.31536 16.0246 6.58942 16.2495 6.77731 16.526C6.93001 16.7509 7.0261 17.0104 7.05712 17.2823H14.1278C14.2022 17.2823 14.2657 17.2531 14.315 17.2049C14.3635 17.1556 14.3924 17.0917 14.3924 17.0173V0.264923C14.3924 0.190235 14.3635 0.127032 14.315 0.0777353C14.2657 0.028829 14.2022 0.000313416 14.1278 -3.8147e-05H4.83517C4.76083 0.000313416 4.69712 0.028829 4.64798 0.0777353C4.59946 0.127032 4.57095 0.190196 4.57056 0.264923V9.27352L5.26415 9.53383C5.37895 9.57778 5.48603 9.63285 5.5854 9.69754ZM9.48142 16.4351C9.22528 16.4351 9.01794 16.2271 9.01794 15.9714C9.01794 15.7153 9.22528 15.5078 9.48142 15.5078C9.73731 15.5078 9.94505 15.7153 9.94505 15.9714C9.94505 16.2271 9.73731 16.4351 9.48142 16.4351Z"
										fill="currentColor"
									/>
									<path
										d="M4.62429 15.3707C4.7589 15.4211 4.89597 15.445 5.03054 15.445C5.26335 15.445 5.49019 15.3737 5.68003 15.2447C5.87026 15.1154 6.02241 14.9293 6.10999 14.6979C6.16046 14.5634 6.18433 14.4264 6.18433 14.2916C6.18433 14.0589 6.11312 13.8317 5.98409 13.6423C5.85491 13.452 5.66929 13.2995 5.43765 13.2121L4.2428 12.7619C4.10843 12.7115 3.97147 12.6876 3.83632 12.6876C3.60374 12.6876 3.37687 12.7588 3.18702 12.8878C2.99698 13.0172 2.84448 13.2032 2.75706 13.4351C2.70624 13.5691 2.68253 13.7062 2.68237 13.8414C2.68237 14.074 2.75397 14.3008 2.883 14.4911C3.01222 14.6814 3.19784 14.8331 3.43003 14.9209L4.62429 15.3707Z"
										fill="currentColor"
									/>
									<path
										d="M4.26427 12.1881C4.39888 12.2397 4.53595 12.2628 4.67052 12.2628C4.90313 12.2628 5.13052 12.192 5.3204 12.0625C5.51024 11.9331 5.66278 11.7479 5.74997 11.5164C5.80083 11.3808 5.82431 11.2441 5.82431 11.109C5.8247 10.8772 5.75329 10.6499 5.62446 10.4597C5.49509 10.2698 5.30927 10.1177 5.0772 10.0299L3.88278 9.58008C3.74798 9.52922 3.61126 9.50539 3.4763 9.50539C3.24372 9.50539 3.01685 9.5766 2.82685 9.70641C2.63696 9.83504 2.48446 10.0211 2.39665 10.2533C2.34642 10.3873 2.32251 10.5244 2.32251 10.6592C2.32251 10.8918 2.39395 11.1186 2.52298 11.3089C2.6522 11.4988 2.83802 11.6513 3.07009 11.739L4.26427 12.1881Z"
										fill="currentColor"
									/>
									<path
										d="M8.85315 7.36488L9.27429 7.09375C9.40929 7.0075 9.44854 6.82805 9.36171 6.69324L8.76612 5.76434C8.67968 5.62957 8.50019 5.59105 8.36522 5.67695C8.36214 5.67883 8.31499 5.70891 7.80526 6.03625C7.2114 6.4175 7.5305 8.05078 8.37812 9.37211C9.22593 10.6934 10.5785 11.6639 11.1719 11.2827C11.6819 10.9557 11.7286 10.9256 11.7319 10.923C11.8667 10.8371 11.906 10.6569 11.8195 10.5221L11.2235 9.59395C11.1373 9.45914 10.9576 9.41988 10.8226 9.50691L10.4013 9.77723C10.2785 9.85578 10.1158 9.83078 10.022 9.7191C10.022 9.7191 9.7389 9.4291 9.31624 8.77016C8.89378 8.11203 8.7478 7.73305 8.7478 7.73305C8.68507 7.60137 8.73034 7.44348 8.85315 7.36488Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15429">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},

					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'CCAPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Monitoring Pipeline Golive',
							},
						],
					},
					{
						key: '#pengajuan-kondisi-khusus',
						label: 'Pengajuan Kondisi Khusus',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.785 8.45494C17.9033 8.45494 18.0162 8.39306 18.0997 8.30963L20.7697 5.63222C20.9435 5.45841 20.9435 5.17303 20.7697 4.99922L18.0997 2.34234C18.0162 2.25891 17.9033 2.22502 17.785 2.22502H13.734V1.3073C13.734 0.570094 13.1803 0 12.4429 0H12.0023C11.2648 0 10.619 0.570094 10.619 1.3073V2.22497H5.7701C5.52414 2.22497 5.27903 2.39433 5.27903 2.64009V7.98005C5.27903 8.22586 5.52414 8.45494 5.7701 8.45494H10.619V9.34495H6.21508C6.09686 9.34495 5.98389 9.37697 5.90046 9.46041L3.23046 12.123C3.05664 12.2968 3.05664 12.5746 3.23046 12.7485L5.90046 15.4315C5.98389 15.515 6.09691 15.5749 6.21508 15.5749H10.619V23.555C10.619 23.8008 10.818 24 11.064 24C11.3099 24 11.509 23.8007 11.509 23.555V15.5749H12.844V23.555C12.844 23.8007 13.043 24 13.2889 24C13.5349 24 13.7339 23.8007 13.7339 23.555V15.5749H18.2299C18.4759 15.5749 18.6288 15.3458 18.6288 15.1V9.76003C18.6288 9.51431 18.4759 9.34491 18.2299 9.34491H13.734V8.45489L17.785 8.45494ZM11.509 1.3073C11.509 1.06069 11.7558 0.890016 12.0022 0.890016H12.4428C12.6893 0.890016 12.844 1.06069 12.844 1.3073V2.22497H11.509V1.3073H11.509ZM17.739 10.2349V14.6849H6.39935L4.17438 12.4599L6.39935 10.235L17.739 10.2349ZM11.509 9.34495V8.45494H12.844V9.34495H11.509ZM6.16905 7.56492V3.11498H11.1101H13.3351H17.6008L19.8258 5.33995L17.6008 7.56492H6.16905Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'NAPListPage',
						label: 'Nota Analisa Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'NAPListPage',
								label: 'NAP',
							},
							{
								key: 'MKLListPage',
								label: 'Memo Kredit Limit',
							},
						],
					},
					{
						key: 'OCRListPage',
						label: 'OCR Rekening',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<rect
									x="2"
									y="2"
									width="12"
									height="16"
									rx="2"
									ry="2"
									stroke="currentColor"
									strokeWidth="1.5"
								/>
								{/* Text Lines */}
								<line
									x1="4"
									y1="5"
									x2="12"
									y2="5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeWidth="1.5"
								/>
								<line
									x1="4"
									y1="8"
									x2="10"
									y2="8"
									stroke="currentColor"
									strokeLinecap="round"
									strokeWidth="1.5"
								/>
								<line
									x1="4"
									y1="11"
									x2="12"
									y2="11"
									stroke="currentColor"
									strokeLinecap="round"
									strokeWidth="1.5"
								/>
								{/* Magnifying Glass */}
								<circle
									cx="15"
									cy="15"
									r="3"
									stroke="currentColor"
									strokeWidth="1.5"
								/>
								<line
									x1="16.8"
									y1="16.8"
									x2="18.5"
									y2="18.5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeWidth="1.5"
								/>
							</svg>
						),
					},
					{
						key: 'SPFListPage',
						label: 'Pemberitahuan Fasilitas',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'MPPListPage',
						label: 'Pengajuan Pencairan Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
				],
			);
			break;
		case 'FH':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHProspectListPage',
						label: 'Prospects',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},

					{
						key: 'FHSurveyApprovalListPage',
						label: 'Survey',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18.8618 6.11264C18.7285 5.79746 18.5068 5.5309 18.2275 5.34181C17.9482 5.15326 17.6082 5.0421 17.2465 5.0421H15.737V4.78167H13.5489C13.5588 4.71089 13.5644 4.63788 13.5644 4.56434C13.5643 3.70171 12.8626 3 12 3C11.1374 3 10.4357 3.70171 10.4357 4.56434C10.4357 4.63788 10.4412 4.71089 10.4512 4.78167H8.26308V5.0421H6.75346C6.51291 5.0421 6.28069 5.09133 6.07111 5.18033C5.75536 5.31361 5.48884 5.53533 5.29972 5.81459C5.11116 6.09438 5 6.43391 5 6.79556V19.3667C5 19.6072 5.04923 19.8389 5.1377 20.049C5.27151 20.3642 5.49326 20.6308 5.77252 20.8199C6.05179 21.009 6.39181 21.1196 6.75346 21.1196H17.2465C17.4871 21.1196 17.7193 21.0709 17.9295 20.9814C18.2446 20.8486 18.5112 20.6264 18.7003 20.3471C18.8888 20.0678 19 19.7278 19 19.3667V6.79556C19 6.55448 18.9508 6.32279 18.8618 6.11264ZM12 3.98373C12.3202 3.98373 12.5806 4.24416 12.5806 4.56434C12.5806 4.6412 12.5651 4.71418 12.5375 4.78167H11.4619C11.4348 4.71421 11.4194 4.6412 11.4194 4.56434C11.4194 4.24416 11.6804 3.98373 12 3.98373ZM17.9571 19.3667C17.9571 19.4662 17.9372 19.558 17.9013 19.6426C17.8482 19.7693 17.7569 19.8787 17.6436 19.9561C17.5297 20.0324 17.3959 20.0767 17.2465 20.0767H6.75346C6.65391 20.0767 6.56158 20.0568 6.47696 20.0214C6.35033 19.9677 6.24084 19.8765 6.16397 19.7631C6.0871 19.6492 6.04287 19.5154 6.04287 19.3667V6.79556C6.04287 6.69601 6.06279 6.60367 6.09871 6.51906C6.15236 6.39187 6.2425 6.28294 6.35639 6.20607C6.47031 6.12977 6.60411 6.08553 6.75342 6.08497H8.26301V6.30229C8.26301 6.71037 8.59369 7.04106 9.00234 7.04106H14.9976C15.4062 7.04106 15.7369 6.71037 15.7369 6.30229V6.08497H17.2465C17.3461 6.08497 17.4384 6.10489 17.523 6.14081C17.6496 6.19446 17.7591 6.2846 17.836 6.39848C17.9123 6.51293 17.9565 6.64621 17.9571 6.79552V19.3667Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 9.12683H8.21936V9.99554H15.7801V9.12683Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 11.5167H8.21936V12.3854H15.7801V11.5167Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 13.9067H8.21936V14.7755H15.7801V13.9067Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 16.948H12V17.8173H15.7801V16.948Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FHVisitReportListPage',
						label: 'Laporan Kunjungan',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.835 5.498L15.252 2.95C15.0452 2.73208 14.7967 2.55785 14.5214 2.43762C14.2461 2.31738 13.9494 2.25359 13.649 2.25H6.768C6.16706 2.25185 5.59125 2.49139 5.16632 2.91632C4.74139 3.34125 4.50185 3.91706 4.5 4.518V18.182C4.50185 18.7829 4.74139 19.3587 5.16632 19.7837C5.59125 20.2086 6.16706 20.4481 6.768 20.45H16.232C16.8329 20.4481 17.4087 20.2086 17.8337 19.7837C18.2586 19.3587 18.4981 18.7829 18.5 18.182V7.101C18.501 6.80306 18.4427 6.5079 18.3285 6.2327C18.2143 5.95751 18.0466 5.70776 17.835 5.498ZM16.813 6.45H14.3V3.937L16.813 6.45ZM17.1 18.182C17.0982 18.4116 17.0061 18.6313 16.8437 18.7937C16.6813 18.9561 16.4616 19.0482 16.232 19.05H6.768C6.53836 19.0482 6.31864 18.9561 6.15625 18.7937C5.99387 18.6313 5.90183 18.4116 5.9 18.182V4.518C5.90183 4.28836 5.99387 4.06864 6.15625 3.90625C6.31864 3.74387 6.53836 3.65183 6.768 3.65H12.9V7.15C12.9 7.33565 12.9737 7.5137 13.105 7.64497C13.2363 7.77625 13.4143 7.85 13.6 7.85H17.1V18.182Z"
									fill="currentColor"
								/>
								<path
									d="M9.40006 11.553C9.26825 11.4212 9.08947 11.3472 8.90306 11.3472C8.71665 11.3472 8.53787 11.4212 8.40606 11.553C8.27425 11.6848 8.2002 11.8636 8.2002 12.05C8.2002 12.2364 8.27425 12.4152 8.40606 12.547L10.1561 14.297C10.2211 14.3626 10.2986 14.4147 10.3839 14.4502C10.4692 14.4858 10.5606 14.5041 10.6531 14.5041C10.7455 14.5041 10.837 14.4858 10.9223 14.4502C11.0076 14.4147 11.085 14.3626 11.1501 14.297L14.6501 10.797C14.7819 10.6578 14.853 10.4719 14.8477 10.2802C14.8425 10.0886 14.7613 9.90683 14.6221 9.77502C14.4828 9.64321 14.2969 9.57211 14.1053 9.57736C13.9136 9.58261 13.7319 9.66378 13.6001 9.80302L10.5971 12.813L9.40006 11.553Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'FHPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Monitoring Pipeline Golive',
							},
							{
								key: 'MonitoringPipelineGolivePage',
								label: 'Dashboard Pipeline Golive',
							},
						],
					},

					{
						key: 'NAPListPage',
						label: 'Nota Analisa Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'NAPListPage',
								label: 'NAP',
							},
							{
								key: 'MKLListPage',
								label: 'Memo Kredit Limit',
							},
						],
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
				],
			);

			break;
		case 'CH':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHProspectListPage',
						label: 'Prospects',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'FBDHCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},

					{
						key: 'CHSurveyApprovalListPage',
						label: 'Survey',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18.8618 6.11264C18.7285 5.79746 18.5068 5.5309 18.2275 5.34181C17.9482 5.15326 17.6082 5.0421 17.2465 5.0421H15.737V4.78167H13.5489C13.5588 4.71089 13.5644 4.63788 13.5644 4.56434C13.5643 3.70171 12.8626 3 12 3C11.1374 3 10.4357 3.70171 10.4357 4.56434C10.4357 4.63788 10.4412 4.71089 10.4512 4.78167H8.26308V5.0421H6.75346C6.51291 5.0421 6.28069 5.09133 6.07111 5.18033C5.75536 5.31361 5.48884 5.53533 5.29972 5.81459C5.11116 6.09438 5 6.43391 5 6.79556V19.3667C5 19.6072 5.04923 19.8389 5.1377 20.049C5.27151 20.3642 5.49326 20.6308 5.77252 20.8199C6.05179 21.009 6.39181 21.1196 6.75346 21.1196H17.2465C17.4871 21.1196 17.7193 21.0709 17.9295 20.9814C18.2446 20.8486 18.5112 20.6264 18.7003 20.3471C18.8888 20.0678 19 19.7278 19 19.3667V6.79556C19 6.55448 18.9508 6.32279 18.8618 6.11264ZM12 3.98373C12.3202 3.98373 12.5806 4.24416 12.5806 4.56434C12.5806 4.6412 12.5651 4.71418 12.5375 4.78167H11.4619C11.4348 4.71421 11.4194 4.6412 11.4194 4.56434C11.4194 4.24416 11.6804 3.98373 12 3.98373ZM17.9571 19.3667C17.9571 19.4662 17.9372 19.558 17.9013 19.6426C17.8482 19.7693 17.7569 19.8787 17.6436 19.9561C17.5297 20.0324 17.3959 20.0767 17.2465 20.0767H6.75346C6.65391 20.0767 6.56158 20.0568 6.47696 20.0214C6.35033 19.9677 6.24084 19.8765 6.16397 19.7631C6.0871 19.6492 6.04287 19.5154 6.04287 19.3667V6.79556C6.04287 6.69601 6.06279 6.60367 6.09871 6.51906C6.15236 6.39187 6.2425 6.28294 6.35639 6.20607C6.47031 6.12977 6.60411 6.08553 6.75342 6.08497H8.26301V6.30229C8.26301 6.71037 8.59369 7.04106 9.00234 7.04106H14.9976C15.4062 7.04106 15.7369 6.71037 15.7369 6.30229V6.08497H17.2465C17.3461 6.08497 17.4384 6.10489 17.523 6.14081C17.6496 6.19446 17.7591 6.2846 17.836 6.39848C17.9123 6.51293 17.9565 6.64621 17.9571 6.79552V19.3667Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 9.12683H8.21936V9.99554H15.7801V9.12683Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 11.5167H8.21936V12.3854H15.7801V11.5167Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 13.9067H8.21936V14.7755H15.7801V13.9067Z"
									fill="currentColor"
								/>
								<path
									d="M15.7801 16.948H12V17.8173H15.7801V16.948Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: 'CHVisitReportListPage',
						label: 'Laporan Kunjungan',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.835 5.498L15.252 2.95C15.0452 2.73208 14.7967 2.55785 14.5214 2.43762C14.2461 2.31738 13.9494 2.25359 13.649 2.25H6.768C6.16706 2.25185 5.59125 2.49139 5.16632 2.91632C4.74139 3.34125 4.50185 3.91706 4.5 4.518V18.182C4.50185 18.7829 4.74139 19.3587 5.16632 19.7837C5.59125 20.2086 6.16706 20.4481 6.768 20.45H16.232C16.8329 20.4481 17.4087 20.2086 17.8337 19.7837C18.2586 19.3587 18.4981 18.7829 18.5 18.182V7.101C18.501 6.80306 18.4427 6.5079 18.3285 6.2327C18.2143 5.95751 18.0466 5.70776 17.835 5.498ZM16.813 6.45H14.3V3.937L16.813 6.45ZM17.1 18.182C17.0982 18.4116 17.0061 18.6313 16.8437 18.7937C16.6813 18.9561 16.4616 19.0482 16.232 19.05H6.768C6.53836 19.0482 6.31864 18.9561 6.15625 18.7937C5.99387 18.6313 5.90183 18.4116 5.9 18.182V4.518C5.90183 4.28836 5.99387 4.06864 6.15625 3.90625C6.31864 3.74387 6.53836 3.65183 6.768 3.65H12.9V7.15C12.9 7.33565 12.9737 7.5137 13.105 7.64497C13.2363 7.77625 13.4143 7.85 13.6 7.85H17.1V18.182Z"
									fill="currentColor"
								/>
								<path
									d="M9.40006 11.553C9.26825 11.4212 9.08947 11.3472 8.90306 11.3472C8.71665 11.3472 8.53787 11.4212 8.40606 11.553C8.27425 11.6848 8.2002 11.8636 8.2002 12.05C8.2002 12.2364 8.27425 12.4152 8.40606 12.547L10.1561 14.297C10.2211 14.3626 10.2986 14.4147 10.3839 14.4502C10.4692 14.4858 10.5606 14.5041 10.6531 14.5041C10.7455 14.5041 10.837 14.4858 10.9223 14.4502C11.0076 14.4147 11.085 14.3626 11.1501 14.297L14.6501 10.797C14.7819 10.6578 14.853 10.4719 14.8477 10.2802C14.8425 10.0886 14.7613 9.90683 14.6221 9.77502C14.4828 9.64321 14.2969 9.57211 14.1053 9.57736C13.9136 9.58261 13.7319 9.66378 13.6001 9.80302L10.5971 12.813L9.40006 11.553Z"
									fill="currentColor"
								/>
							</svg>
						),
					},

					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'CHPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Monitoring Pipeline Golive',
							},
							{
								key: 'MonitoringPipelineGolivePage',
								label: 'Dashboard Pipeline Golive',
							},
						],
					},

					{
						key: 'NAPListPage',
						label: 'Nota Analisa Pembiayaan',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.69814L15.5363 6.31194C16.1761 5.67206 17.2136 5.67206 17.8534 6.31194C18.4933 6.95181 18.4933 7.98926 17.8534 8.62911L17.4673 9.01536M15.15 6.69814C15.15 6.69814 15.1983 7.51881 15.9224 8.24294C16.6466 8.96703 17.4673 9.01536 17.4673 9.01536M15.15 6.69814L11.5995 10.2486C11.359 10.4891 11.2388 10.6094 11.1353 10.7419C11.0134 10.8984 10.9088 11.0676 10.8235 11.2466C10.7512 11.3984 10.6973 11.5597 10.5898 11.8824L10.2455 12.9154L10.1341 13.2495M17.4673 9.01536L13.9168 12.5659C13.6763 12.8064 13.556 12.9266 13.4234 13.03C13.267 13.1519 13.0978 13.2565 12.9188 13.3419C12.7669 13.4142 12.6057 13.468 12.283 13.5755L11.25 13.9199L10.9158 14.0313M10.1341 13.2495L10.0227 13.5838C9.96976 13.7425 10.0111 13.9176 10.1294 14.0359C10.2478 14.1543 10.4228 14.1956 10.5816 14.1427L10.9158 14.0313M10.1341 13.2495L10.9158 14.0313"
									stroke="currentColor"
								/>
								<path
									d="M6.66699 10.832H8.75033"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 7.5H12.0837"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M6.66699 14.168H7.91699"
									stroke="currentColor"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64428C15.5474 1.66797 13.976 1.66797 10.8333 1.66797H9.16667C6.02397 1.66797 4.45262 1.66797 3.47631 2.64428C2.5 3.62059 2.5 5.19193 2.5 8.33463V11.668C2.5 14.8106 2.5 16.382 3.47631 17.3583C4.45262 18.3346 6.02397 18.3346 9.16667 18.3346H10.8333C13.976 18.3346 15.5474 18.3346 16.5237 17.3583C17.3096 16.5724 17.4628 15.401 17.4928 13.3346"
									stroke="currentColor"
									strokeLinecap="round"
								/>
							</svg>
						),
						children: [
							{
								key: 'NAPListPage',
								label: 'NAP',
							},
							{
								key: 'MKLListPage',
								label: 'Memo Kredit Limit',
							},
						],
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
				],
			);
			break;
		case 'FS':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'FRMCustomerGroupListPage',
						label: 'Group BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: 'FSCustomerListPage',
						label: 'Data CES Customer',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5352 14.6426C15.8711 14.2168 16.0723 13.6797 16.0723 13.0938C16.0723 11.7129 14.9531 10.5938 13.5723 10.5938C12.1914 10.5938 11.0723 11.7129 11.0723 13.0938C11.0723 13.6777 11.2734 14.2168 11.6094 14.6426C10.2305 15.3555 9.28711 16.7949 9.28711 18.4512H10.7148C10.7148 16.875 11.9961 15.5938 13.5723 15.5938C15.1484 15.5938 16.4297 16.875 16.4297 18.4512H17.8574C17.8574 16.7969 16.9121 15.3574 15.5352 14.6426ZM13.5723 12.0234C14.1621 12.0234 14.6445 12.5039 14.6445 13.0957C14.6445 13.6855 14.1641 14.168 13.5723 14.168C12.9824 14.168 12.5 13.6875 12.5 13.0957C12.5 12.5039 12.9805 12.0234 13.5723 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14258 1.42773V18.5723H8.57227V17.1426H3.57227V2.85742H16.4297V10H17.8574V1.42773H2.14258Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'FRMPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Monitoring Pipeline Golive',
							},
							{
								key: 'MonitoringPipelineGolivePage',
								label: 'Dashboard Pipeline Golive',
							},
						],
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
				],
			);

			break;
		case 'FCS':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'PlakatListPage',
						label: 'Pencetakan Plakat',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.875 1.875V5.625H15.625L11.875 1.875ZM11.875 1.875H4.375V18.125H15.625V5.625"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 8.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11.25 11.25H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M10 13.75H13.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 7.5V8.125"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.125 11.875V12.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M8.75 8.125H7.8125C7.3125 8.125 6.875 8.5625 6.875 9.0625C6.875 9.5625 7.3125 10 7.8125 10H8.4375C8.9375 10 9.375 10.4375 9.375 10.9375C9.375 11.4375 8.9375 11.875 8.4375 11.875H7.5"
									stroke="currentColor"
									strokeMiterlimit="10"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
				],
			);
			break;
		case 'SECOM':
			mainMenu.push(
				...[
					{
						key: 'DashboardPage',
						label: 'Dashboard',
						icon: (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006C18.3224 8.40006 18.0001 8.72242 18.0001 9.12006H19.4401ZM11.0401 18.0001C10.6424 18.0001 10.3201 18.3224 10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401V18.0001ZM18.0001 9.12006C18.0001 9.5177 18.3224 9.84006 18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006H18.0001ZM11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006C10.3201 5.6777 10.6424 6.00006 11.0401 6.00006V4.56006ZM18.7201 9.84006C19.1177 9.84006 19.4401 9.5177 19.4401 9.12006C19.4401 8.72242 19.1177 8.40006 18.7201 8.40006V9.84006ZM11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006V8.40006ZM6.00006 12.0001C6.00006 11.6024 5.6777 11.2801 5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001H6.00006ZM11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201C11.7601 18.3224 11.4377 18.0001 11.0401 18.0001V19.4401ZM4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201C5.6777 12.7201 6.00006 12.3977 6.00006 12.0001H4.56006ZM11.0401 6.00006C11.4377 6.00006 11.7601 5.6777 11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006V6.00006ZM5.28006 11.2801C4.88242 11.2801 4.56006 11.6024 4.56006 12.0001C4.56006 12.3977 4.88242 12.7201 5.28006 12.7201V11.2801ZM11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801V12.7201ZM11.7601 12.0001C11.7601 11.6024 11.4377 11.2801 11.0401 11.2801C10.6424 11.2801 10.3201 11.6024 10.3201 12.0001H11.7601ZM10.3201 18.7201C10.3201 19.1177 10.6424 19.4401 11.0401 19.4401C11.4377 19.4401 11.7601 19.1177 11.7601 18.7201H10.3201ZM10.3201 12.0001C10.3201 12.3977 10.6424 12.7201 11.0401 12.7201C11.4377 12.7201 11.7601 12.3977 11.7601 12.0001H10.3201ZM11.7601 9.12006C11.7601 8.72242 11.4377 8.40006 11.0401 8.40006C10.6424 8.40006 10.3201 8.72242 10.3201 9.12006H11.7601ZM11.7601 5.28006C11.7601 4.88242 11.4377 4.56006 11.0401 4.56006C10.6424 4.56006 10.3201 4.88242 10.3201 5.28006H11.7601ZM10.3201 9.12006C10.3201 9.5177 10.6424 9.84006 11.0401 9.84006C11.4377 9.84006 11.7601 9.5177 11.7601 9.12006H10.3201ZM18.0001 9.12006V14.8801H19.4401V9.12006H18.0001ZM18.0001 14.8801C18.0001 16.6032 16.6032 18.0001 14.8801 18.0001V19.4401C17.3985 19.4401 19.4401 17.3985 19.4401 14.8801H18.0001ZM14.8801 18.0001H11.0401V19.4401H14.8801V18.0001ZM19.4401 9.12006C19.4401 6.60164 17.3985 4.56006 14.8801 4.56006V6.00006C16.6032 6.00006 18.0001 7.39693 18.0001 9.12006H19.4401ZM14.8801 4.56006H11.0401V6.00006H14.8801V4.56006ZM18.7201 8.40006H11.0401V9.84006H18.7201V8.40006ZM4.56006 12.0001V14.8801H6.00006V12.0001H4.56006ZM4.56006 14.8801C4.56006 17.3985 6.60164 19.4401 9.12006 19.4401V18.0001C7.39693 18.0001 6.00006 16.6032 6.00006 14.8801H4.56006ZM9.12006 19.4401H11.0401V18.0001H9.12006V19.4401ZM6.00006 12.0001V9.12006H4.56006V12.0001H6.00006ZM6.00006 9.12006C6.00006 7.39693 7.39693 6.00006 9.12006 6.00006V4.56006C6.60164 4.56006 4.56006 6.60164 4.56006 9.12006H6.00006ZM9.12006 6.00006H11.0401V4.56006H9.12006V6.00006ZM5.28006 12.7201H11.0401V11.2801H5.28006V12.7201ZM10.3201 12.0001V18.7201H11.7601V12.0001H10.3201ZM11.7601 12.0001V9.12006H10.3201V12.0001H11.7601ZM10.3201 5.28006V9.12006H11.7601V5.28006H10.3201Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
					{
						key: 'SCRisalahListPage',
						label: 'Risalah Pemutusan Plafon',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.15 6.6996L15.5363 6.3134C16.1761 5.67353 17.2136 5.67353 17.8534 6.3134C18.4933 6.95328 18.4933 7.99073 17.8534 8.63058L17.4673 9.01683M15.15 6.6996C15.15 6.6996 15.1983 7.52028 15.9224 8.2444C16.6466 8.96849 17.4673 9.01683 17.4673 9.01683M15.15 6.6996L11.5995 10.2501C11.359 10.4906 11.2388 10.6108 11.1353 10.7434C11.0134 10.8998 10.9088 11.0691 10.8235 11.2481C10.7512 11.3999 10.6973 11.5612 10.5898 11.8838L10.2455 12.9168L10.1341 13.251M17.4673 9.01683L13.9168 12.5673C13.6763 12.8078 13.556 12.9281 13.4234 13.0315C13.267 13.1534 13.0978 13.258 12.9188 13.3433C12.7669 13.4157 12.6057 13.4695 12.283 13.577L11.25 13.9213L10.9158 14.0327M10.1341 13.251L10.0227 13.5852C9.96976 13.744 10.0111 13.9191 10.1294 14.0374C10.2478 14.1557 10.4228 14.1971 10.5816 14.1442L10.9158 14.0327M10.1341 13.251L10.9158 14.0327"
									stroke="currentColor"
									strokeWidth="1.5"
								/>
								<path
									d="M6.6665 10.8335H8.74984"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
								<path
									d="M6.6665 7.5H12.0832"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
								<path
									d="M6.6665 14.1665H7.9165"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
								<path
									d="M16.5237 2.64281C15.5474 1.6665 13.976 1.6665 10.8333 1.6665H9.16667C6.02397 1.6665 4.45262 1.6665 3.47631 2.64281C2.5 3.61913 2.5 5.19047 2.5 8.33317V11.6665C2.5 14.8092 2.5 16.3806 3.47631 17.3568C4.45262 18.3332 6.02397 18.3332 9.16667 18.3332H10.8333C13.976 18.3332 15.5474 18.3332 16.5237 17.3568C17.3096 16.5709 17.4628 15.3995 17.4928 13.3332"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
							</svg>
						),
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
				],
			);

			break;

		case 'BOD':
			mainMenu.push(
				...[
					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'MonitoringPipelineGolivePage',
								label: 'Dashboard Pipeline Golive',
							},
						],
					},
				],
			);
			break;

		case 'CR':
			mainMenu.push(
				...[
					{
						key: 'CRCustomerGroupListPage',
						label: 'Group Customer BMPP',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_1251_15551)">
									<path
										d="M10.0469 12.5819V9.94702"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M12.4736 14.0227L10.1719 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M7.625 14.0227L9.92672 12.7402"
										stroke="currentColor"
										strokeMiterlimit="10"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M10.8308 5.69834V5.61553C11.4642 5.32573 11.9131 4.72511 12.0108 4.03728C12.2779 3.94042 12.4662 3.68247 12.4662 3.39038C12.4662 3.13893 12.3303 2.91383 12.1201 2.79377C12.142 2.72528 12.1599 2.65622 12.1738 2.58725C12.2856 2.03253 12.1582 1.46562 11.815 0.990944C11.4774 0.52391 10.9698 0.201855 10.3859 0.0840881C10.2289 0.0524214 10.0688 0.036377 9.9101 0.036377C9.40929 0.036377 8.92067 0.197899 8.53424 0.491144C8.13627 0.793077 7.86413 1.22205 7.76795 1.69912C7.69502 2.0608 7.72482 2.43394 7.85372 2.78402C7.63522 2.90145 7.49052 3.13233 7.49052 3.39047C7.49052 3.68634 7.6816 3.9452 7.95259 4.0399C8.05298 4.74407 8.51653 5.35041 9.17074 5.63301V5.6984C7.81194 6.0062 6.875 7.02256 6.875 8.2103V8.52751H8.05762V8.52694H8.18896V8.52751H13.1266V8.2103C13.1266 7.02252 12.1897 6.0062 10.8308 5.69834ZM9.91011 0.670855C10.0266 0.670855 10.1446 0.682699 10.2606 0.70601C10.6875 0.792121 11.057 1.02532 11.3009 1.36265C11.5283 1.67715 11.6199 2.047 11.5613 2.40992C11.2491 2.26425 10.8388 2.11023 10.4072 2.05111C9.59584 1.94017 9.58286 1.90249 9.53448 1.76225L9.3796 1.31399L9.02327 1.62924C8.98534 1.66263 8.69827 1.91499 8.36697 2.18947C8.35784 2.06772 8.36548 1.94565 8.38993 1.82443C8.52466 1.156 9.164 0.670855 9.91011 0.670855ZM8.56591 2.84659C8.78842 2.66659 9.00822 2.4807 9.16726 2.3443C9.38766 2.54312 9.73138 2.59904 10.3211 2.67971C10.7212 2.73444 11.1135 2.89463 11.3972 3.03533V3.75024C11.3972 4.53082 10.7622 5.16592 9.98161 5.16592C9.20098 5.16592 8.56592 4.53082 8.56592 3.75024V2.84659H8.56591ZM9.80518 5.79252C9.93638 5.80408 10.0668 5.8027 10.1964 5.78904V5.9849L10.0002 6.0936L9.80519 5.98523V5.79252H9.80518ZM7.54181 7.8927C7.68742 7.18223 8.31131 6.59163 9.17074 6.35245V6.35858L9.9997 6.81911L10.8308 6.35881V6.35245C11.6903 6.59163 12.3143 7.18237 12.4598 7.89298L8.18632 7.89245V7.89269L7.54181 7.8927Z"
										fill="currentColor"
									/>
									<path
										d="M4.18233 17.1346V17.0518C4.81576 16.762 5.26471 16.1613 5.36242 15.4735C5.62944 15.3767 5.81775 15.1187 5.81775 14.8266C5.81775 14.5751 5.68192 14.3501 5.47168 14.23C5.49356 14.1615 5.51151 14.0924 5.52541 14.0235C5.63721 13.4688 5.50979 12.9019 5.1666 12.4272C4.82893 11.9602 4.32145 11.6381 3.73754 11.5204C3.58056 11.4887 3.42042 11.4727 3.26166 11.4727C2.76086 11.4727 2.27223 11.6342 1.88581 11.9274C1.48783 12.2294 1.21565 12.6583 1.11946 13.1354C1.04658 13.4971 1.07638 13.8703 1.20528 14.2203C0.986785 14.3377 0.842074 14.5686 0.842074 14.8267C0.842074 15.1226 1.03316 15.3815 1.30411 15.4761C1.4045 16.1803 1.86805 16.7867 2.52231 17.0693V17.1346C1.16351 17.4425 0.226562 18.4588 0.226562 19.6465V19.9638H1.40918V19.9625H1.54047V19.9638H6.47817V19.6465C6.47817 18.4588 5.54123 17.4425 4.18233 17.1346ZM3.26167 12.1071C3.37821 12.1071 3.49617 12.1189 3.61217 12.1423C4.0391 12.2284 4.40856 12.4616 4.65242 12.7989C4.87995 13.1135 4.97151 13.4834 4.9129 13.8462C4.60064 13.7005 4.1903 13.5465 3.75874 13.4874C2.94741 13.3764 2.93442 13.3387 2.88598 13.1985L2.73115 12.7503L2.37457 13.0657C2.31301 13.1199 2.03796 13.3612 1.71855 13.6257C1.70938 13.504 1.71702 13.382 1.74147 13.2607C1.87623 12.5923 2.51554 12.1071 3.26167 12.1071ZM1.91748 14.2828C2.13985 14.1029 2.3597 13.917 2.51883 13.7805C2.7392 13.9794 3.08296 14.0353 3.6727 14.116C4.07283 14.1707 4.46503 14.3309 4.74876 14.4716V15.1865C4.74876 15.9671 4.11371 16.6021 3.33317 16.6021C2.55255 16.6021 1.91748 15.9671 1.91748 15.1865V14.2828ZM3.15675 17.2288C3.28804 17.2404 3.41818 17.239 3.54795 17.2253V17.4212L3.35176 17.5298L3.15676 17.4215V17.2288H3.15675ZM1.29217 19.329V19.3293H0.893296C1.03877 18.6186 1.6628 18.0279 2.52232 17.7887V17.7949L3.35127 18.2553L4.18233 17.795V17.7887C5.04191 18.0279 5.66588 18.6186 5.81135 19.3293L1.5379 19.3287V19.329L1.29217 19.329Z"
										fill="currentColor"
									/>
									<path
										d="M17.4753 17.1346V17.0518C18.1088 16.762 18.5577 16.1613 18.6554 15.4735C18.9225 15.3767 19.1108 15.1187 19.1108 14.8266C19.1108 14.5752 18.9749 14.3501 18.7647 14.23C18.7866 14.1615 18.8045 14.0925 18.8184 14.0235C18.9302 13.4688 18.8028 12.9019 18.4596 12.4272C18.122 11.9602 17.6145 11.6381 17.0306 11.5204C16.8736 11.4887 16.7135 11.4727 16.5546 11.4727C16.0539 11.4727 15.5652 11.6342 15.1788 11.9274C14.7809 12.2294 14.5087 12.6583 14.4125 13.1354C14.3396 13.4971 14.3694 13.8702 14.4983 14.2203C14.2798 14.3377 14.1351 14.5686 14.1351 14.8267C14.1351 15.1226 14.3262 15.3815 14.5972 15.4761C14.6976 16.1803 15.1611 16.7867 15.8153 17.0693V17.1346C14.4565 17.4425 13.5195 18.4588 13.5195 19.6465V19.9638H14.7022V19.9632H14.8335V19.9638H19.7711V19.6465C19.7711 18.4588 18.8342 17.4425 17.4753 17.1346ZM16.5546 12.1071C16.6712 12.1071 16.7891 12.1189 16.9052 12.1423C17.3321 12.2284 17.7015 12.4616 17.9454 12.7989C18.173 13.1135 18.2646 13.4835 18.206 13.8462C17.8937 13.7005 17.4834 13.5465 17.0517 13.4874C16.2404 13.3764 16.2274 13.3387 16.1791 13.1985L16.0242 12.7503L15.6669 13.0663C15.6051 13.1207 15.3294 13.3625 15.0115 13.6257C15.0024 13.504 15.0101 13.3819 15.0345 13.2607C15.1692 12.5923 15.8085 12.1071 16.5546 12.1071ZM15.2105 14.2828C15.4335 14.1024 15.653 13.9167 15.8118 13.7805C16.0322 13.9794 16.3759 14.0353 16.9657 14.116C17.3657 14.1707 17.758 14.3309 18.0418 14.4716V15.1865C18.0418 15.9671 17.4068 16.6021 16.6262 16.6021C15.8456 16.6021 15.2105 15.9671 15.2105 15.1865L15.2105 14.2828ZM16.4498 17.2288C16.5809 17.2404 16.7112 17.2389 16.8409 17.2253V17.4212L16.6448 17.5298L16.4498 17.4215V17.2288ZM14.1863 19.329C14.332 18.6185 14.9558 18.0279 15.8153 17.7887V17.7949L16.6443 18.2553L17.4753 17.795V17.7887C18.335 18.0279 18.9589 18.6186 19.1044 19.3293L14.8308 19.3287V19.329L14.1863 19.329Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_1251_15551">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
					},
					{
						key: '#pipeline-management',
						label: 'Pipeline Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_5135_15401)">
									<path
										d="M5.44455 10.4444H14.5557V12.1556H15.4446V9.55556H10.4446V7.77778H9.55567V9.55556H4.55566V12.1556H5.44455V10.4444Z"
										fill="currentColor"
									/>
									<path
										d="M7.77775 12.7778H2.22219C1.92751 12.7778 1.64489 12.8948 1.43652 13.1032C1.22815 13.3116 1.11108 13.5942 1.11108 13.8889V17.2222C1.11108 17.5169 1.22815 17.7995 1.43652 18.0079C1.64489 18.2163 1.92751 18.3333 2.22219 18.3333H7.77775C8.07243 18.3333 8.35505 18.2163 8.56342 18.0079C8.7718 17.7995 8.88886 17.5169 8.88886 17.2222V13.8889C8.88886 13.5942 8.7718 13.3116 8.56342 13.1032C8.35505 12.8948 8.07243 12.7778 7.77775 12.7778ZM2.22219 17.2222V13.8889H7.77775V17.2222H2.22219Z"
										fill="currentColor"
									/>
									<path
										d="M17.7777 12.7778H12.2222C11.9275 12.7778 11.6449 12.8948 11.4365 13.1032C11.2281 13.3116 11.1111 13.5942 11.1111 13.8889V17.2222C11.1111 17.5169 11.2281 17.7995 11.4365 18.0079C11.6449 18.2163 11.9275 18.3333 12.2222 18.3333H17.7777C18.0724 18.3333 18.3551 18.2163 18.5634 18.0079C18.7718 17.7995 18.8889 17.5169 18.8889 17.2222V13.8889C18.8889 13.5942 18.7718 13.3116 18.5634 13.1032C18.3551 12.8948 18.0724 12.7778 17.7777 12.7778ZM12.2222 17.2222V13.8889H17.7777V17.2222H12.2222Z"
										fill="currentColor"
									/>
									<path
										d="M7.22219 7.22222H12.7777C13.0724 7.22222 13.355 7.10516 13.5634 6.89679C13.7718 6.68841 13.8889 6.4058 13.8889 6.11111V2.77778C13.8889 2.48309 13.7718 2.20048 13.5634 1.9921C13.355 1.78373 13.0724 1.66667 12.7777 1.66667H7.22219C6.92751 1.66667 6.64489 1.78373 6.43652 1.9921C6.22815 2.20048 6.11108 2.48309 6.11108 2.77778V6.11111C6.11108 6.4058 6.22815 6.68841 6.43652 6.89679C6.64489 7.10516 6.92751 7.22222 7.22219 7.22222ZM7.22219 2.77778H12.7777V6.11111H7.22219V2.77778Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_5135_15401">
										<rect
											width="20"
											height="20"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						),
						children: [
							{
								key: 'CHPipelineListPage',
								label: 'Pipeline Hot Prospect',
							},
							{
								key: 'GoliveListPage',
								label: 'Pipeline Golive',
							},
							{
								key: 'MonitoringPipelineGolivePage',
								label: 'Dashboard Pipeline Golive',
							},
						],
					},
					{
						key: 'DMSListPage',
						label: 'Document Management System',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9.16406 9.16683H17.4974C17.7184 9.16683 17.9304 9.07903 18.0866 8.92275C18.2429 8.76647 18.3307 8.55451 18.3307 8.3335V3.0835C18.3307 2.86248 18.2429 2.65052 18.0866 2.49424C17.9304 2.33796 17.7184 2.25016 17.4974 2.25016H15.0807L14.5649 1.27683C14.4942 1.14333 14.3885 1.03156 14.2592 0.953466C14.1299 0.875377 13.9818 0.833909 13.8307 0.833496H9.16406C8.94305 0.833496 8.73109 0.921293 8.57481 1.07757C8.41853 1.23385 8.33073 1.44582 8.33073 1.66683V4.16683H3.33073V1.66683C3.33073 1.44582 3.24293 1.23385 3.08665 1.07757C2.93037 0.921293 2.71841 0.833496 2.4974 0.833496C2.27638 0.833496 2.06442 0.921293 1.90814 1.07757C1.75186 1.23385 1.66406 1.44582 1.66406 1.66683V15.0002C1.66406 15.2212 1.75186 15.4331 1.90814 15.5894C2.06442 15.7457 2.27638 15.8335 2.4974 15.8335H8.33073V18.3335C8.33073 18.5545 8.41853 18.7665 8.57481 18.9228C8.73109 19.079 8.94305 19.1668 9.16406 19.1668H17.4974C17.7184 19.1668 17.9304 19.079 18.0866 18.9228C18.2429 18.7665 18.3307 18.5545 18.3307 18.3335V13.0835C18.3307 12.8625 18.2429 12.6505 18.0866 12.4942C17.9304 12.338 17.7184 12.2502 17.4974 12.2502H15.0807L14.5649 11.2768C14.4942 11.1433 14.3885 11.0316 14.2592 10.9535C14.1299 10.8754 13.9818 10.8339 13.8307 10.8335H9.16406C8.94305 10.8335 8.73109 10.9213 8.57481 11.0776C8.41853 11.2339 8.33073 11.4458 8.33073 11.6668V14.1668H3.33073V5.8335H8.33073V8.3335C8.33073 8.55451 8.41853 8.76647 8.57481 8.92275C8.73109 9.07903 8.94305 9.16683 9.16406 9.16683ZM9.99739 12.5002H13.3307L13.8457 13.4735C13.9166 13.6074 14.0227 13.7194 14.1525 13.7975C14.2823 13.8756 14.4309 13.9169 14.5824 13.9168H16.6641V17.5002H9.99739V12.5002ZM9.99739 2.50016H13.3307L13.8457 3.4735C13.9165 3.60712 14.0223 3.71898 14.1518 3.79707C14.2812 3.87517 14.4295 3.91657 14.5807 3.91683H16.6641V7.50016H9.99739V2.50016Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
				],
			);
			break;

		case 'ADMIN':
			mainMenu.push(
				...[
					{
						key: 'AdminDashboardPage',
						label: 'Dashboard Admin',
					},
					{
						key: 'AdminCustomerListPage',
						label: 'Customer List',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M13.3333 17.5V15.8333C13.3333 14.9493 12.9821 14.1014 12.357 13.4763C11.7319 12.8512 10.8841 12.5 10 12.5H4.16667C3.28261 12.5 2.43476 12.8512 1.80964 13.4763C1.18452 14.1014 0.833333 14.9493 0.833333 15.8333V17.5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M7.08333 9.16667C8.92428 9.16667 10.4167 7.67428 10.4167 5.83333C10.4167 3.99238 8.92428 2.5 7.08333 2.5C5.24238 2.5 3.75 3.99238 3.75 5.83333C3.75 7.67428 5.24238 9.16667 7.08333 9.16667Z"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M19.1667 17.5V15.8333C19.1662 15.1098 18.9272 14.4064 18.4877 13.8344C18.0482 13.2625 17.433 12.8546 16.7383 12.675"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M13.3333 2.60834C14.0309 2.78652 14.6489 3.19453 15.0903 3.76747C15.5317 4.34042 15.7719 5.04546 15.7719 5.77084C15.7719 6.49622 15.5317 7.20126 15.0903 7.77421C14.6489 8.34714 14.0309 8.75515 13.3333 8.93334"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						),
					},
					{
						key: '#user-management',
						label: 'User Management',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15.5371 14.6426C15.873 14.2168 16.0742 13.6797 16.0742 13.0938C16.0742 11.7129 14.9551 10.5938 13.5742 10.5938C12.1934 10.5938 11.0742 11.7129 11.0742 13.0938C11.0742 13.6777 11.2754 14.2168 11.6113 14.6426C10.2324 15.3555 9.28906 16.7949 9.28906 18.4512H10.7168C10.7168 16.875 11.998 15.5938 13.5742 15.5938C15.1504 15.5938 16.4316 16.875 16.4316 18.4512H17.8594C17.8594 16.7969 16.9141 15.3574 15.5371 14.6426ZM13.5742 12.0234C14.1641 12.0234 14.6465 12.5039 14.6465 13.0957C14.6465 13.6855 14.166 14.168 13.5742 14.168C12.9844 14.168 12.502 13.6875 12.502 13.0957C12.502 12.5039 12.9824 12.0234 13.5742 12.0234Z"
									fill="currentColor"
								/>
								<path
									d="M2.14062 1.42773V18.5723H8.57031V17.1426H3.57031V2.85742H16.4277V10H17.8555V1.42773H2.14062Z"
									fill="currentColor"
								/>
								<path
									d="M5 5H15V6.42773H5V5ZM5 7.85742H12.1426V9.28516H5V7.85742ZM5 10.7148H9.28516V12.1426H5V10.7148Z"
									fill="currentColor"
								/>
							</svg>
						),
						children: [
							{
								key: 'AdminUserListPage',
								label: 'User List',
							},
							{
								key: 'AdminOrgStructurePage',
								label: 'Organisation Structure',
							},
						],
					},
					{
						key: '#master-data',
						label: 'Master Data',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M17.5 5H2.5C1.81 5 1.25 5.56 1.25 6.25V8.75C1.25 9.44 1.81 10 2.5 10H17.5C18.19 10 18.75 9.44 18.75 8.75V6.25C18.75 5.56 18.19 5 17.5 5ZM17.5 13.75V11.25H2.5V13.75H17.5ZM17.5 16.25H2.5V18.75C2.5 19.44 3.06 20 3.75 20H16.25C16.94 20 17.5 19.44 17.5 18.75V16.25ZM2.5 1.25V3.75H17.5V1.25C17.5 0.56 16.94 0 16.25 0H3.75C3.06 0 2.5 0.56 2.5 1.25Z"
									fill="currentColor"
								/>
							</svg>
						),
						children: [
							{
								key: 'AdminBouwheerListPage',
								label: 'Master Bouwheer',
							},
							{
								key: 'AdminIndustrySectorListPage',
								label: 'Master Industry Sector',
							},
						],
					},
					{
						key: 'AdminEmailLogsPage',
						label: 'Email Logs',
						icon: (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M16.6667 3.33333H3.33333C2.4125 3.33333 1.66667 4.07917 1.66667 5V15C1.66667 15.9208 2.4125 16.6667 3.33333 16.6667H16.6667C17.5875 16.6667 18.3333 15.9208 18.3333 15V5C18.3333 4.07917 17.5875 3.33333 16.6667 3.33333ZM16.6667 5L10 9.16667L3.33333 5H16.6667ZM16.6667 15H3.33333V6.66667L10 10.8333L16.6667 6.66667V15Z"
									fill="currentColor"
								/>
							</svg>
						),
					},
				],
			);

			break;
	}

	mainMenu.push({
		key: '#monitoring',
		label: 'Monitoring',
		icon: (
			<svg
				width="20"
				height="20"
				viewBox="0 0 20 20"
				fill="none"
			>
				<path
					d="M1.875 3.75V13.125C1.875 13.8125 2.4375 14.375 3.125 14.375H16.875C17.5625 14.375 18.125 13.8125 18.125 13.125V3.75C18.125 3.0625 17.5625 2.5 16.875 2.5H3.125C2.4375 2.5 1.875 3.0625 1.875 3.75Z"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M5.625 18.125H14.375"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M8.125 14.375C8.125 15.6875 7.6875 17.25 7 18.125"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M13 18.125C12.3125 17.25 11.875 15.6875 11.875 14.375"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M6.875 6.25C5.5 6.25 4.375 7.375 4.375 8.75C4.375 10.125 5.5 11.25 6.875 11.25C8.25 11.25 9.375 10.125 9.375 8.75"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M7.5 5.625V8.125H10C10 6.75 8.875 5.625 7.5 5.625Z"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M12.5 7.5H15.625"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M14.375 9.375H15.625"
					stroke="currentColor"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		),
		children: [
			{
				key: 'MonitoringSLAListPage',
				label: 'Monitoring Prospect/SLA',
			},
			{
				key: 'MonitoringPlafondListPage',
				label: 'Monitoring Plafon Exp',
			},
			{
				key: 'MonitoringCovenantListPage',
				label: 'Monitoring Covenant',
			},
			{
				key: 'MonitoringDocumentListPage',
				label: 'Monitoring Dokumen',
			},
		],
	});

	return (
		<Layout
			style={{
				minHeight: '100vh',
				paddingTop: 100,
				paddingLeft: isMobile ? 0 : 300,
			}}
		>
			<Layout.Header
				style={{
					display: 'flex',
					alignItems: 'center',
					position: 'fixed',
					inset: '0 0 auto 0',
					zIndex: 100,
				}}
			>
				{isMobile && (
					<button
						onClick={() => setDrawerOpen(true)}
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 44,
							height: 44,
							marginRight: 12,
							flexShrink: 0,
							background: 'transparent',
							border: 'none',
							color: 'white',
							fontSize: 20,
							cursor: 'pointer',
						}}
					>
						<MenuOutlined />
					</button>
				)}
				<Typography.Title style={{ margin: 0, color: 'inherit', fontSize: 30 }}>
					Fleet Dashboard System
					<div
						style={{
							fontSize: 12,
							lineHeight: 1,
							fontWeight: 400,
							opacity: 0.5,
						}}
					>
						{page?.name}
					</div>
					<div
						style={{
							fontSize: 12,
							lineHeight: 1,
							fontWeight: 400,
							opacity: 0.5,
						}}
					>
						v{appVersion}
					</div>
				</Typography.Title>
				<Flex
					flex={1}
					align="center"
					justify="end"
					style={{ lineHeight: 1 }}
					gap={45}
				>
					{(
						[
							{
								label: 'Home',
								pageName: 'DashboardPage',
								icon: (
									<svg
										width="18"
										height="16"
										viewBox="0 0 18 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M17.25 9.52926L9 3.12576L0.75 9.52926V6.92002L9 0.515015L17.25 6.91851V9.52926ZM15.1875 9.29676V15.485H11.0625V11.36H6.9375V15.485H2.8125V9.29751L9 4.65726L15.1875 9.29676Z"
											fill="white"
										/>
									</svg>
								),
							},
							{
								label: 'Logout',
								pageName: 'LoginPage',
								icon: (
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M7.5 12C7.5 11.8011 7.57902 11.6103 7.71967 11.4697C7.86032 11.329 8.05109 11.25 8.25 11.25H15V6.375C15 4.875 13.4161 3.75 12 3.75H4.875C4.17904 3.75074 3.51179 4.02755 3.01967 4.51967C2.52755 5.01179 2.25074 5.67904 2.25 6.375V17.625C2.25074 18.321 2.52755 18.9882 3.01967 19.4803C3.51179 19.9725 4.17904 20.2493 4.875 20.25H12.375C13.071 20.2493 13.7382 19.9725 14.2303 19.4803C14.7225 18.9882 14.9993 18.321 15 17.625V12.75H8.25C8.05109 12.75 7.86032 12.671 7.71967 12.5303C7.57902 12.3897 7.5 12.1989 7.5 12Z"
											fill="white"
										/>
										<path
											d="M21.5302 11.4698L17.7802 7.71982C17.6384 7.5851 17.4495 7.51111 17.254 7.51361C17.0584 7.51612 16.8715 7.59492 16.7332 7.73322C16.5949 7.87152 16.5161 8.05838 16.5136 8.25395C16.5111 8.44952 16.5851 8.63833 16.7198 8.78013L19.1892 11.25H15V12.75H19.1892L16.7198 15.2198C16.6473 15.2888 16.5892 15.3716 16.5492 15.4633C16.5091 15.555 16.4878 15.6538 16.4865 15.7539C16.4852 15.854 16.504 15.9534 16.5417 16.0461C16.5794 16.1389 16.6353 16.2231 16.7061 16.2939C16.7769 16.3647 16.8611 16.4206 16.9539 16.4583C17.0466 16.496 17.1459 16.5148 17.246 16.5135C17.3461 16.5122 17.445 16.4909 17.5367 16.4508C17.6284 16.4107 17.7112 16.3527 17.7802 16.2801L21.5302 12.5301C21.6707 12.3895 21.7497 12.1988 21.7497 12C21.7497 11.8011 21.6707 11.6105 21.5302 11.4698Z"
											fill="white"
										/>
									</svg>
								),
							},
							{
								label: userData?.fullName ?? '-',
								pageName: 'AccountPage',
								icon: (
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M22 12C22 6.49 17.51 2 12 2C6.49 2 2 6.49 2 12C2 14.9 3.25 17.51 5.23 19.34C5.23 19.35 5.23 19.35 5.22 19.36C5.32 19.46 5.44 19.54 5.54 19.63C5.6 19.68 5.65 19.73 5.71 19.77C5.89 19.92 6.09 20.06 6.28 20.2C6.35 20.25 6.41 20.29 6.48 20.34C6.67 20.47 6.87 20.59 7.08 20.7C7.15 20.74 7.23 20.79 7.3 20.83C7.5 20.94 7.71 21.04 7.93 21.13C8.01 21.17 8.09 21.21 8.17 21.24C8.39 21.33 8.61 21.41 8.83 21.48C8.91 21.51 8.99 21.54 9.07 21.56C9.31 21.63 9.55 21.69 9.79 21.75C9.86 21.77 9.93 21.79 10.01 21.8C10.29 21.86 10.57 21.9 10.86 21.93C10.9 21.93 10.94 21.94 10.98 21.95C11.32 21.98 11.66 22 12 22C12.34 22 12.68 21.98 13.01 21.95C13.05 21.95 13.09 21.94 13.13 21.93C13.42 21.9 13.7 21.86 13.98 21.8C14.05 21.79 14.12 21.76 14.2 21.75C14.44 21.69 14.69 21.64 14.92 21.56C15 21.53 15.08 21.5 15.16 21.48C15.38 21.4 15.61 21.33 15.82 21.24C15.9 21.21 15.98 21.17 16.06 21.13C16.27 21.04 16.48 20.94 16.69 20.83C16.77 20.79 16.84 20.74 16.91 20.7C17.11 20.58 17.31 20.47 17.51 20.34C17.58 20.3 17.64 20.25 17.71 20.2C17.91 20.06 18.1 19.92 18.28 19.77C18.34 19.72 18.39 19.67 18.45 19.63C18.56 19.54 18.67 19.45 18.77 19.36C18.77 19.35 18.77 19.35 18.76 19.34C20.75 17.51 22 14.9 22 12ZM16.94 16.97C14.23 15.15 9.79 15.15 7.06 16.97C6.62 17.26 6.26 17.6 5.96 17.97C4.44 16.43 3.5 14.32 3.5 12C3.5 7.31 7.31 3.5 12 3.5C16.69 3.5 20.5 7.31 20.5 12C20.5 14.32 19.56 16.43 18.04 17.97C17.75 17.6 17.38 17.26 16.94 16.97Z"
											fill="white"
										/>
										<path
											d="M12 6.92969C9.93 6.92969 8.25 8.60969 8.25 10.6797C8.25 12.7097 9.84 14.3597 11.95 14.4197C11.98 14.4197 12.02 14.4197 12.04 14.4197C12.06 14.4197 12.09 14.4197 12.11 14.4197C12.12 14.4197 12.13 14.4197 12.13 14.4197C14.15 14.3497 15.74 12.7097 15.75 10.6797C15.75 8.60969 14.07 6.92969 12 6.92969Z"
											fill="white"
										/>
									</svg>
								),
							},
						] as const
					).map(({ label, icon, pageName }) => (
						<a
							key={pageName}
							href="#"
							style={{
								display: 'flex',
								gap: 6,
								alignItems: 'center',
								color: 'inherit',
							}}
							onClick={async (e) => {
								e.preventDefault();
								if (pageName === 'LoginPage') {
									await unauthenticate({ logout: true });
								}
								navigate(pageName);
							}}
						>
							{icon}
							<span>{label}</span>
						</a>
					))}
				</Flex>
			</Layout.Header>

			<Layout hasSider={!isMobile}>
				{(() => {
					const menuContent = (
						<Flex
							justify="space-between"
							align="stretch"
							style={{ height: '100%' }}
							vertical
						>
							<Menu
								mode="inline"
								style={{
									height: '100%',
									borderRight: 0,
									overflow: 'auto',
								}}
								defaultOpenKeys={['#prescreening']}
								items={[...mainMenu].map((menu) => {
									return {
										...menu,
									};
								})}
								onClick={(props) => {
									if (props.key in PAGE_COMPONENT) {
										navigate(props.key as PageName);
									} else {
										navigate('NotFoundPage');
									}
									setDrawerOpen(false);
								}}
							/>
						</Flex>
					);

					return isMobile ? (
						<Drawer
							open={drawerOpen}
							onClose={() => setDrawerOpen(false)}
							placement='left'
							width={300}
							styles={{ body: { padding: 0 } }}
						>
							{menuContent}
						</Drawer>
					) : (
						<Layout.Sider
							trigger={null}
							collapsible={true}
							width={300}
							style={{
								position: 'fixed',
								inset: '100px auto 0 0',
							}}
							translate="no"
						>
							{menuContent}
						</Layout.Sider>
					);
				})()}
				<Layout>
					<Layout.Content
						style={{
							padding: '20px 40px',
						}}
					>
						{children}
					</Layout.Content>
				</Layout>
			</Layout>

			{/* Debug Panel */}
			<DebugPanel />
		</Layout>
	);
}
